import React from 'react';
import CurrencyFormat from 'react-currency-format';
import { getUserLocale as getUserLocaleFromBrowser } from 'get-user-locale';
import {
  ORDER_STATUS_CANCELLED,
  ORDER_STATUS_COMPLETED,
  ORDER_STATUS_DELIVERING,
  ORDER_STATUS_DISPUTED,
  ORDER_STATUS_PAID,
  ORDER_STATUS_PREPARED,
  ORDER_STATUS_PREPARING,
  ORDER_STATUS_REFUNDED,
  ORDER_STATUS_PENDING,
  ORDER_TYPE_DELIVERY,
  ORDER_TYPE_INHOUSE,
  ORDER_TYPE_SELF_SERVICE,
  ORDER_TYPE_PICKUP,
  ORDER_STATUS_PROCESSING,
  ORDER_TYPE_CATERING,
  CHECKOUT_METHOD_PAY_AND_GO,
  CHECKOUT_METHOD_ORDER_AND_PAY,
  ROUTE_STORE,
  ROUTE_TABLE,
  ROUTE_KIOSK,
  CHECKOUT_METHOD_ORDER_ONLY,
} from '../constants';
import * as MD5 from 'md5.js';
import axios from 'axios';
import {
  APP_FALLBACK_LOCALE,
  APP_SUPPORTED_LANGUAGES,
  config,
} from '../../configs/app.config';
import { fetchPlaceDetails } from '../hooks/usePlacesAutocomplete';
import {
  baseDeleteQuery,
  baseGetQuery,
  basePatchQuery,
  prepareQueryParams,
  buildV1QueryString,
} from '../sb-api/sb-api.service';
import { DateTime } from 'luxon';
import { debounce } from 'debounce';

export const getBrowserLocale = () => {
  let determinedLocale = getUserLocaleFromBrowser({
    fallbackLocale: APP_FALLBACK_LOCALE,
    useFallbackLocale: true,
  });

  // Sanitize locale - remove POSIX extensions (e.g., en-US@posix -> en-US)
  if (determinedLocale && determinedLocale.includes('@')) {
    determinedLocale = determinedLocale.split('@')[0];
  }

  const targetLocale = APP_SUPPORTED_LANGUAGES.map((language) =>
    language.toLowerCase(),
  ).includes(determinedLocale.toLowerCase().substring(0, 2))
    ? determinedLocale
    : APP_FALLBACK_LOCALE;

  return targetLocale;
};

export const formatEuro = (value) => {
  return (
    <CurrencyFormat
      displayType="text"
      decimalSeparator=","
      thousandSeparator="."
      decimalScale={2}
      fixedDecimalScale={true}
      value={value}
      suffix={' €'}
    />
  );
};

export const createRedirect = (redirectUrl) => {
  localStorage.setItem('redirectUrl', redirectUrl || window.location.pathname);
  return window.location.origin + '/redirect';
};

// returns locales in a hierarchical array which represents the order of locales for the current user
export const getLocaleHierarchy = (locale) => {
  const currentLocale = (locale || APP_FALLBACK_LOCALE)
    .toUpperCase()
    .substring(0, 2);

  return [
    currentLocale,
    ...['EN', 'DE', 'ES', 'IT', 'FR', 'HU'].filter(
      (locale) => locale !== currentLocale,
    ),
  ];
};

// Helper to get value from a translations object { de: "...", en: "...", ... }
// using the locale hierarchy
const getValueFromTranslationsObject = (translationsObj, locale) => {
  if (!translationsObj || typeof translationsObj !== 'object') return null;

  // Go through locale hierarchy and return first non-empty value
  return getLocaleHierarchy(locale).reduce((result, localeSuffix) => {
    if (result) return result;
    const localeKey = localeSuffix.toLowerCase();
    return translationsObj[localeKey] || '';
  }, '');
};

// Expecting that an object has either:
// 1. New format: property is an object with locale keys like { name: { de: "...", en: "...", ... } }
// 2. Legacy format: properties like nameDE, nameFR, nameIT, ...
// This function returns the first non-nullish value using the order from getLocaleHierarchy
// returns null or the value
export const getLocalizedDataProp = (data, propPrefix, locale) => {
  if (!data || !propPrefix) return null;

  // Check if the property exists as an object (new format)
  const propValue = data[propPrefix];
  if (propValue && typeof propValue === 'object' && !Array.isArray(propValue)) {
    // New format: { name: { de: "...", en: "...", ... } }
    return getValueFromTranslationsObject(propValue, locale);
  }

  // Legacy format: nameDE, nameFR, nameIT, ...
  return getLocaleHierarchy(locale).reduce(
    (result, propSuffix) => result || data[propPrefix + propSuffix] || '',
    '',
  );
};

export const getLocalizedOrderHint = (data, locale) =>
  // go through hierarchy and keep the first non-null data prop value
  // This handles objects like { de: "...", en: "...", ... }
  data
    ? getLocaleHierarchy(locale).reduce((result, prop) => {
        return result || data[prop.toLowerCase()] || '';
      }, '')
    : null;

// returns false or the name
export const getLocalizedName = (data, locale) =>
  data && getLocalizedDataProp(data, 'name', locale);

export const getLocalizedCategoryName = (data, locale) =>
  data && getLocalizedDataProp(data, 'category', locale);

// returns false or the description
export const getLocalizedDescription = (data, locale) =>
  data && getLocalizedDataProp(data, 'description', locale);

export const getLocalizedTipTitle = (data, locale) =>
  data && getLocalizedDataProp(data, 'tipTitle', locale);

// returns false or the category
export const getLocalizedCategory = (data, locale) =>
  data && getLocalizedDataProp(data, 'category', locale);

export const getLocalizedTitle = (data, locale) =>
  data && getLocalizedDataProp(data, 'title', locale);

// returns false or the image url
export const getImageUrl = (image, variant = 'public') =>
  image && `${config.cfImages.baseUrl}/${image.cfImagesId}/${variant}`;

// returns public or the image variant
export const getImageVariant = (baseVariant) => {
  const ratio =
    Math.min(3, Math.max(1, Math.round(window.devicePixelRatio || '2'))) + 'x'; // => 1x, 2x, 3x,
  return baseVariant ? baseVariant + ratio : 'public';
};

/* export const trimText = (inputText, maxLength = 20, suffix = '...') => {
  if (inputText) {
    const roughlyTrimmedText = inputText.substr(0, maxLength);
    return roughlyTrimmedText === inputText
      ? inputText
      : roughlyTrimmedText.substr(
          0,
          Math.min(
            roughlyTrimmedText.length,
            roughlyTrimmedText.lastIndexOf(' '),
          ),
        ) + (suffix || '');
  } else {
    return '';
  }
}; */

export const trimText = (inputText, maxLength = 30, suffix = '...') => {
  if (inputText) {
    const roughlyTrimmedText = inputText.substr(0, maxLength);
    if (roughlyTrimmedText.length < inputText.length) {
      let lastIndex = roughlyTrimmedText.lastIndexOf(' ');
      if (lastIndex === -1 || lastIndex < maxLength - suffix.length) {
        lastIndex = maxLength - suffix.length;
      }
      return roughlyTrimmedText.substr(0, lastIndex) + suffix;
    } else {
      return inputText;
    }
  } else {
    return '';
  }
};

// returns an array of ids from a given array of objects
export const extractIds = (data) => data?.map(({ id }) => id) || [];

// returns the orderClass for a given orderType
export const getOrderClass = (orderType) => {
  switch (orderType) {
    case ORDER_TYPE_INHOUSE:
    case ORDER_TYPE_SELF_SERVICE:
      return 'inhouse';
    case ORDER_TYPE_PICKUP:
    case ORDER_TYPE_DELIVERY:
    case ORDER_TYPE_CATERING:
      return 'takeaway';
    default:
      console.debug(`No orderClass found for orderType="${orderType}"`);
      return null;
  }
};

// returns true, if orderType is Takeaway-ish
export const isTakeawayOrderClass = (orderType) =>
  getOrderClass(orderType) === 'takeaway';

// returns true, if orderType is Inhouse-ish
export const isInhouseOrderClass = (orderType) =>
  getOrderClass(orderType) === 'inhouse';

// returns an ItemData's / ItemAddition's price, depending on the orderType. Returns NaN on failure.
export const getItemDataOrAdditionPrice = (orderType, object) =>
  (orderType &&
    object &&
    (isInhouseOrderClass(orderType)
      ? object.priceInhouse
      : object.priceTakeaway)) ??
  NaN;

// returns an ItemData's / ItemAddition's tax, depending on the orderType. Returns NaN on failure.
export const getItemDataOrAdditionTaxByOrderType = (orderType, object) =>
  (orderType &&
    object &&
    (isInhouseOrderClass(orderType)
      ? object.taxInhouse
      : object.taxTakeaway)) ??
  NaN;

// Check if any order config is available and enabled for ordering
// Used to determine if plus icons should be shown
export const hasAnyOrderConfigEnabled = (store, table = null) => {
  if (!store) return false;

  // If on a table, check inhouse order config
  if (table) {
    const inhouseConfig = getOrderConfig(store, ORDER_TYPE_INHOUSE);
    if (inhouseConfig?.isEnabled) return true;
  }

  // Check pickup and self-service order configs (available without table)
  const pickupConfig = getOrderConfig(store, ORDER_TYPE_PICKUP);
  const selfServiceConfig = getOrderConfig(store, ORDER_TYPE_SELF_SERVICE);

  return pickupConfig?.isEnabled || selfServiceConfig?.isEnabled;
};

// Check if ordering is possible (any order config enabled + within service times or pre-order allowed)
export const canOrder = (store, table = null) => {
  if (!store) return false;

  // Check if any order config is enabled
  if (!hasAnyOrderConfigEnabled(store, table)) {
    return false;
  }

  // Check if within service times
  const isWithinServiceTimes = isOpenAt(
    store.openingHours,
    new Date(),
    store.timeZone || 'Europe/Berlin',
  );
  if (isWithinServiceTimes) return true;

  // If not within service times, check if pre-order is allowed for pickup/delivery/catering
  const pickupConfig = getOrderConfig(store, ORDER_TYPE_PICKUP);
  const deliveryConfig = getOrderConfig(store, ORDER_TYPE_DELIVERY);
  const cateringConfig = getOrderConfig(store, ORDER_TYPE_CATERING);

  // Pre-order is allowed if any of these configs are enabled and have preOrderHours > 0
  if (pickupConfig?.isEnabled && pickupConfig?.preOrderHours > 0) return true;
  if (deliveryConfig?.isEnabled && deliveryConfig?.preOrderHours > 0)
    return true;
  if (cateringConfig?.isEnabled && cateringConfig?.preOrderHours > 0)
    return true;

  return false;
};

// returns true, if an ItemData can be ordered
export const isOrderEnabled = (
  store,
  itemData,
  cart,
  checkout,
  table = null,
  kiosk = null,
) => {
  if (!(store && itemData)) return false; // during render, these properties will become available

  const { paymentProcessor } = store || {};
  const itemDataSuffix = `(itemDataId=${itemData.id})(${getLocalizedName(
    itemData,
  )})`;

  // If no checkout/orderType is set yet, check if any ordering is possible
  if (!checkout?.orderType) {
    // Show plus icon if any order config is enabled and we can order
    const canOrderResult = canOrder(store, table);
    if (!canOrderResult) {
      console.debug(
        `Ordering disabled: no enabled OrderConfig or outside service times ${itemDataSuffix}`,
      );
    }
    return canOrderResult;
  }

  // Get the active order config for the current order type
  const orderConfig = getOrderConfig(store, checkout.orderType);
  const posIntegration = orderConfig?.posIntegration;

  // Check if order config exists and is enabled
  if (!orderConfig) {
    console.debug(
      `Ordering disabled: no OrderConfig found for orderType="${checkout.orderType}" ${itemDataSuffix}`,
    );
    return false;
  }

  if (!orderConfig.isEnabled) {
    console.debug(
      `Ordering disabled: OrderConfig is not enabled for orderType="${checkout.orderType}" ${itemDataSuffix}`,
    );
    return false;
  }

  // For kiosk without terminal, only order_only checkout method is allowed
  if (kiosk && !kiosk.terminal) {
    const allowedMethods = orderConfig.allowedCheckoutMethods || [];
    if (
      checkout.checkoutMethod !== CHECKOUT_METHOD_ORDER_ONLY ||
      !allowedMethods.includes(CHECKOUT_METHOD_ORDER_ONLY)
    ) {
      console.debug(`Ordering disabled: no terminal`);
      return false;
    }
  }

  // check if orderType and checkoutMethod are set
  if (!checkout.orderType || !checkout.checkoutMethod) {
    console.debug(
      `Ordering disabled: orderType="${checkout.orderType}"; checkoutMethod="${checkout.checkoutMethod}" are invalid ${itemDataSuffix}`,
    );
    return false;
  } else if (
    !kiosk &&
    checkout.checkoutMethod === CHECKOUT_METHOD_ORDER_AND_PAY &&
    !paymentProcessor?.canCharge
  ) {
    console.debug(
      `Ordering disabled: checkoutMethod="${checkout.checkoutMethod}" requires paymentProcessor.canCharge=true ${itemDataSuffix}`,
    );
    return false;
  }
  if (table?.area && !table?.area?.isActive) {
    console.debug(`Ordering disabled: table area disabled`);
    return false;
  }
  // check posIntegrationRef
  if (posIntegration && !itemData.posIntegrationRef) {
    console.debug(
      `Ordering disabled: itemData.posIntegrationRef is not set ${itemDataSuffix}`,
    );
    return false;
  } else if (posIntegration && table && !table.posIntegrationRef) {
    console.debug(
      `Ordering disabled: table.posIntegrationRef is not set ${itemDataSuffix}`,
    );
    return false;
  } else if (posIntegration && !posIntegration?.isActive) {
    console.debug(`Ordering disabled: posIntegration is not active`);
    return false;
  }

  const isPreOrderType = [
    ORDER_TYPE_PICKUP,
    ORDER_TYPE_DELIVERY,
    ORDER_TYPE_CATERING,
  ].includes(checkout.orderType);

  // Availability gate only (isActive + order class + service time). Quantity and
  // total limits are intentionally NOT enforced here: doing so makes min
  // constraints (minQty/minTotal) unsatisfiable, because the cart can never
  // grow past the first unit (the gate would block every further add). Those
  // limits are validated when adding (upper bounds) and at the cart/checkout
  // step via getCartOrderConditionViolations, mirroring the backend/kiosk.

  // Pre-order types (pickup/delivery/catering): the customer selects a future
  // fulfillment time, so before one is chosen an item is orderable as long as
  // its OrderCondition service times cover some upcoming moment. This
  // deliberately ignores whether the store or the window is active *right now*,
  // so e.g. an item available 9–13 can be pre-ordered before 9. Once a time is
  // chosen, the item must be available at exactly that time.
  if (isPreOrderType) {
    if (
      !isOrderConditionScopeAvailable(
        itemData?.orderConditions,
        checkout.orderType,
        getEffectiveRequestedAt(checkout),
      )
    ) {
      console.debug(
        `Ordering disabled: No satisfiable OrderCondition for the requested time ${itemDataSuffix}`,
      );
      return false;
    }
    return true;
  }

  // Inhouse / self-service: the item must be orderable right now.
  if (!isOpenAt(store.openingHours, new Date(), store.timeZone || 'Europe/Berlin')) {
    console.debug(`Ordering disabled: Store is closed ${itemDataSuffix}`);
    return false;
  }

  if (!hasSatisfiedOrderCondition(itemData?.orderConditions, checkout.orderType)) {
    console.debug(
      `Ordering disabled: No available OrderCondition found ${itemDataSuffix}`,
    );
    return false;
  }

  return true;
};

// returns the total for an array of OrderItems. Returns NaN on failure.
export const getOrderItemsTotal = (orderType, orderItems) =>
  (orderItems || []).reduce(
    (sum, orderItem) => sum + getOrderItemTotal(orderType, orderItem),
    0,
  );

export const calculateCartTotal = getOrderItemsTotal;

// returns the total for a single OrderItem, depending on orderType. Returns NaN on failure.
// set singlePrice=true to force qty=1
export const getOrderItemTotal = (orderType, orderItem, singlePrice = false) =>
  (singlePrice ? 1 : orderItem.qty) *
  ((orderItem.price ??
    getItemDataOrAdditionPrice(orderType, orderItem.itemData)) +
    getOrderItemAdditionsTotal(orderType, orderItem.additions));

// returns the total for an array of OrderItemAdditions, depending on orderType. Returns NaN on failure.
// OrderItem's price property is calculated by the backend. This function will handle both cases, BEFORE sending to backend and AFTER.
export const getOrderItemAdditionsTotal = (orderType, orderItemAdditions) => {
  if ((orderItemAdditions || []).every(({ price }) => price !== undefined)) {
    return (orderItemAdditions || []).reduce(
      (sum, orderItemAddition) =>
        sum + getOrderItemAdditionTotal(orderItemAddition),
      0,
    );
  } else {
    // each AdditionGroupEntry NEEDS to have a group attached
    orderItemAdditions.forEach(({ additionGroupEntry }) => {
      if (!additionGroupEntry?.group?.id) {
        throw new Error(
          `Group is missing for AdditionGroupEntry (id=${additionGroupEntry.id})`,
        );
      }
    });

    // extract unique ItemAdditionGroup IDs
    const uniqueGroupIds = [
      ...new Set(
        orderItemAdditions.map(
          ({ additionGroupEntry }) => additionGroupEntry.group.id,
        ),
      ),
    ];

    // get the total for a group
    const getTotalForItemAdditionGroup = (groupId) => {
      const orderItemAdditionsFromGroup = orderItemAdditions.filter(
        ({ additionGroupEntry }) => additionGroupEntry.group.id === groupId,
      );
      const freeOfChargeSelections =
        orderItemAdditionsFromGroup[0]?.additionGroupEntry.group
          .freeOfChargeSelections || 0; // take group from first addition as it's the same for all additions

      let currentQty = 1;
      let total = 0;

      orderItemAdditionsFromGroup.forEach(({ qty, additionGroupEntry }) => {
        Array.from({ length: qty }).forEach(() => {
          if (currentQty > freeOfChargeSelections) {
            total += getItemDataOrAdditionPrice(
              orderType,
              additionGroupEntry.addition,
            );
          } // else: it's free! No need to add anything to total
          currentQty++;
        });
      });

      return total;
    };

    return uniqueGroupIds.reduce(
      (sum, groupId) => sum + getTotalForItemAdditionGroup(groupId),
      0,
    );
  }
};

// calculates total for a single OrderItemAddition. NEEDS TO HAVE a price, otherwise returns NaN
export const getOrderItemAdditionTotal = (orderItemAddition) =>
  (orderItemAddition.qty - orderItemAddition.freeOfChargeQty) *
  (orderItemAddition.price ?? NaN);

// returns true, if mobile device
export const isMobileDevice = () => window.innerWidth <= 768;

// extracts the Store's slug from me
export const extractMyStoreSlug = (me) => extractMyStore(me, 'slug');

// extracts the Store object from me (considers onBehalfOfStore for admins)
export const extractMyStore = (me, prop) => {
  // Priority: onBehalfOfStore > employeeProfile.store > requester.employeeProfile.store
  const store =
    me?.onBehalfOfStore ||
    me?.employeeProfile?.store ||
    me?.requester?.employeeProfile?.store;
  return store && prop && store[prop] ? store[prop] : store;
};

// Helper to check if user is a store owner (has storeId or merchantProfileId)
export const isStoreOwner = (me) => {
  return me?.isStoreOwner === true || !!(me?.merchantProfileId || me?.storeId);
};

// Helper to check if a string is a valid UUID
export const isUUID = (str) => {
  if (!str || typeof str !== 'string') return false;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

// Helper to get storeId from URL query param
export const getStoreIdFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('storeId');
};

// Helper to build URL with storeId query param preserved (for admin/manager on-behalf-of mode)
// Usage: buildUrlWithStoreId('/dashboard') => '/dashboard?storeId=xxx' if storeId is in current URL
export const buildUrlWithStoreId = (path, storeId = null) => {
  const currentStoreId = storeId || getStoreIdFromUrl();
  if (!currentStoreId) return path;

  // Check if path already has query params
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}storeId=${currentStoreId}`;
};

// returns true, if user is in admin/manager mode (accessing store with storeId query param or own store)
export const isAdminMode = (me, slug) => {
  // Admin or manager accessing store with storeId query param
  const storeIdFromUrl = getStoreIdFromUrl();
  if ((me?.isAdmin || me?.storeId) && storeIdFromUrl) {
    return true;
  }
  // Store owner accessing their own store (by slug match or just having storeId/merchantProfileId)
  if (isStoreOwner(me)) {
    // If slug is provided, check if it matches the store's slug
    const myStoreSlug = extractMyStoreSlug(me);
    if (slug && myStoreSlug) {
      return myStoreSlug === slug;
    }
    // Store owner without slug check - they're in admin mode for their store
    return true;
  }
  // Admin with onBehalfOfStore
  if (me?.onBehalfOfStore) {
    if (slug) {
      return me.onBehalfOfStore.slug === slug;
    }
    return true;
  }
  return false;
};

export const getOrderPathType = (orderType) => {
  switch (orderType) {
    case ORDER_TYPE_INHOUSE:
      return 'inhouse';
    case ORDER_TYPE_SELF_SERVICE:
      return 'self-service';
    case ORDER_TYPE_PICKUP:
      return 'pickup';
    case ORDER_TYPE_DELIVERY:
      return 'delivery';
    case ORDER_TYPE_CATERING:
      return 'catering';
    default:
      console.debug(`Cannot determine path type for orderType="${orderType}"`);
      return null;
  }
};

// The notification method config types customers can be notified through.
export const NOTIFICATION_METHOD_CONFIG_TYPES = [
  'WhatsAppNotificationMethodConfig',
  'SmsNotificationMethodConfig',
  'PagerNotificationMethodConfig',
  'EmailNotificationMethodConfig',
];

// Enabled notification method configs for an order config. Accepts the raw
// store config value (which may still be a string id before it is loaded).
export const getEnabledNotificationMethodConfigs = (orderConfig) => {
  if (!orderConfig || typeof orderConfig !== 'object') return [];
  return (orderConfig.notificationMethodConfigs || []).filter(
    (m) => m?.isEnabled && NOTIFICATION_METHOD_CONFIG_TYPES.includes(m.type),
  );
};

// Whether the order config exposes at least one enabled notification method
// (WhatsApp, SMS, Pager or Email). Single source of truth for the checkout,
// tip, order-info and post-order prompts.
export const hasEnabledNotificationMethods = (orderConfig) =>
  getEnabledNotificationMethodConfigs(orderConfig).length > 0;

// returns the cookie settings
export const getCookieSettings = () => ({
  allowNecessary: localStorage.getItem('allowNecessaryCookies') || false,
  allowFunctional: localStorage.getItem('allowFunctionalCookies') || false,
  allowMarketing: localStorage.getItem('allowMarketingCookies') || false,
});

// updates the cookie settings
export const setCookieSettings = (
  allowNecessary,
  allowFunctional,
  allowMarketing,
) => {
  localStorage.setItem('allowNecessaryCookies', allowNecessary || false);
  localStorage.setItem('allowFunctionalCookies', allowFunctional || false);
  localStorage.setItem('allowMarketingCookies', allowMarketing || false);
};

// creates a hash string of given data (any type). Returns null on failure.
export const createHash = (data) =>
  (data &&
    new MD5()
      .update(typeof data !== 'string' ? JSON.stringify(data) : data)
      .digest('hex')) ||
  null;

// sets the request defaults (axios, etc.)
export const setRequestDefaults = (
  token,
  isGuest = false,
  setWebSocketToken,
) => {
  // set default timeout to 60 sec
  axios.defaults.timeout = 60 * 1000;

  // needed for Development Environment (CF Access)
  axios.defaults.withCredentials = true;

  // set sb-bend url as baseUrl
  // THIS IS NOT WORKING see https://github.com/nuxt-community/axios-module/issues/508
  /*axios.defaults.baseURL = config.apiBaseUrl;*/

  if (token) {
    const targetToken = (!isGuest ? 'Bearer ' : '') + token;
    // auth0 tokens are bearer tokens, otherwise guest token
    axios.defaults.headers.common['Authorization'] = targetToken;
    if (setWebSocketToken) {
      setWebSocketToken(targetToken);
    }
  } else {
    // unauthenticated user (just visitor)
    delete axios.defaults.headers.common['Authorization'];
  }

  if (isGuest) {
    localStorage.setItem('guestToken', token);
  }
};

// returns an object, where each property is a group defined by the keyProperty
export const groupByKey = (array, keyProp) =>
  array.reduce(
    (res, obj) => ({
      ...res,
      [obj[keyProp]]: (res[obj[keyProp]] || []).concat(obj),
    }),
    {},
  );

// returns a StoreLink or an empty string on failure
export const getStoreLink = (store, isShort = false) =>
  (store &&
    ((isShort && config.shortUrl && `${config.shortUrl}/${store.slug}`) ||
      `${window.location.origin}${ROUTE_STORE}/${store.slug}`)) ||
  '';

// returns a TableLink or an empty string on failure
export const getTableLink = (table, isShort = false) =>
  (table &&
    ((isShort && config.shortUrl && `${config.shortUrl}/t-${table.slug}`) ||
      `${window.location.origin}${ROUTE_TABLE}/${table.slug}`)) ||
  '';
export const getKioskLink = (kiosk) =>
  (kiosk && `${window.location.origin}${ROUTE_KIOSK}/${kiosk.id}`) || '';

export const buildStoreManifestUrl = (store, locale) => {
  const manifest = {
    name: store?.name,
    short_name: store?.name,
    description: getLocalizedDescription(store?.menu, locale),
    background_color: '#000000',
    theme_color: '#0f4a73',
    icons: [
      {
        src: getImageUrl(store?.logo, 'androidIcon192x192'),
        sizes: '192x192',
      },
      {
        src: getImageUrl(store?.logo, 'androidIcon512x512'),
        sizes: '512x512',
      },
    ],
  };
  const stringManifest = JSON.stringify(manifest);
  const blob = new Blob([stringManifest], { type: 'application/json' });

  return URL.createObjectURL(blob);
};

// capitalizes a string
export const capitalize = (text) =>
  (text && text[0].toUpperCase() + text.slice(1)) || '';

export const extractOrderTaxesByType = (order) => {
  if (!order) return null;

  // collect all tax types
  const taxPerItem = order.items.flatMap(({ qty, itemData, additions }) => {
    const itemDataPrice = getItemDataOrAdditionPrice(order.type, itemData) || 0;
    const itemDataTax =
      getItemDataOrAdditionTaxByOrderType(order.type, itemData) || 0;

    return [
      {
        type: itemDataTax,
        amount: qty * ((itemDataPrice / (itemDataTax + 100)) * itemDataTax),
      },
      ...additions?.map((addition) => {
        const additionsPrice =
          getItemDataOrAdditionPrice(order.type, addition?.itemAddition) || 0;
        const additionsTax =
          getItemDataOrAdditionTaxByOrderType(
            order.type,
            addition?.itemAddition,
          ) || 0;

        return {
          type: additionsTax,
          amount:
            (additionsPrice / (additionsTax + 100)) *
            additionsTax *
            qty *
            addition?.qty,
        };
      }),
    ];
  });

  const taxTypes = [...new Set(taxPerItem.map(({ type }) => type))];

  return taxTypes
    .filter((taxType) => !!taxType) // exclude 0% taxes
    .map((taxType) => ({
      type: taxType,
      total: taxPerItem
        .filter(({ type }) => type === taxType)
        .reduce((total, { amount }) => total + amount, 0),
    }))
    .sort((a, b) => a.type - b.type); // sort asc
};

export const getOrderStatusPillColor = ({ status }) => {
  switch (status) {
    case ORDER_STATUS_PENDING:
    case ORDER_STATUS_PAID:
      return 'light-dark';
    case ORDER_STATUS_PREPARING:
    case ORDER_STATUS_PROCESSING:
      return 'light-warning';
    case ORDER_STATUS_PREPARED:
    case ORDER_STATUS_DELIVERING:
    case ORDER_STATUS_COMPLETED:
    case ORDER_STATUS_REFUNDED:
      return 'light-success';
    case ORDER_STATUS_CANCELLED:
    case ORDER_STATUS_DISPUTED:
      return 'light-danger';
    default:
      return null;
  }
};

export const getOrderTypePillColor = ({ type }) => {
  switch (type) {
    case ORDER_TYPE_INHOUSE:
      return 'light-primary';
    case ORDER_TYPE_SELF_SERVICE:
      return 'light-primary';
    case ORDER_TYPE_DELIVERY:
      return 'light-warning';
    case ORDER_TYPE_PICKUP:
      return 'light-danger';
    default:
      return null;
  }
};

// returns one Date object per month (local TZ)
export const getMonthsInRange = (startDate, endDate) => {
  const currentDate = new Date(
    startDate.getFullYear(),
    startDate.getMonth(),
    1,
  );
  const dates = [];
  while (currentDate <= endDate) {
    dates.push(new Date(currentDate));
    currentDate.setMonth(currentDate.getMonth() + 1);
  }

  return dates;
};

export const getDaysInRange = (startDate, endDate) => {
  // Normalize the endDate to the start of the day (00:00) to include the full day
  const normalizedEndDate = new Date(endDate);
  normalizedEndDate.setHours(23, 59, 59, 999); // Ensure endDate includes the full day

  const currentDate = new Date(startDate);
  const dates = [];

  while (currentDate <= normalizedEndDate) {
    dates.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1); // Move to the next day
  }

  return dates;
};

// returns first Date of month (local TZ)
export const getFirstDateOfMonth = (date, monthDelta = 0, x = 1) =>
  new Date(date.getFullYear(), date.getMonth() + monthDelta, x);

// returns last Date of month (local TZ)
export const getLastDateOfMonth = (date, monthDelta = 0) =>
  new Date(
    date.getFullYear(),
    date.getMonth() + monthDelta + 1,
    0,
    23,
    59,
    59,
    999,
  );

export const getPaymentMethodDetails = (paymentMethod) => {
  if (paymentMethod?.type) {
    const baseIconPath = '/assets/icons/payment-logos/';
    switch (paymentMethod?.type) {
      case 'card':
        return {
          name: 'Credit Card',
          iconSrc: baseIconPath + paymentMethod.card.brand + '.svg',
          suffix: '**** ' + paymentMethod.card.last4,
          expiresAt:
            paymentMethod.card.exp_month + '/' + paymentMethod.card.exp_year,
        };
      case 'sepa_debit':
        return {
          name: 'Sepa Debit',
          iconSrc: baseIconPath + 'elv.svg',
          suffix: '**** ' + paymentMethod.sepa_debit.last4,
          expiresAt: null,
        };
      case 'paypal':
        return {
          name: 'PayPal',
          iconSrc: baseIconPath + 'paypal.svg',
          suffix: paymentMethod.paypal.payer_email,
          expiresAt: null,
        };
      case 'klarna':
        return {
          name: 'Klarna',
          iconSrc: baseIconPath + 'klarna.svg',
          suffix: paymentMethod.klarna.payer_email,
          expiresAt: null,
        };
      default:
        break;
    }
  } else {
    return {};
  }
};

export const getNextOrderStatus = (currentStatus, orderType) => {
  switch (currentStatus) {
    case ORDER_STATUS_PENDING:
      return ORDER_STATUS_PAID;
    case ORDER_STATUS_PROCESSING:
      return ORDER_STATUS_PREPARING;
    case ORDER_STATUS_PREPARING:
      return ORDER_STATUS_PREPARED;
    case ORDER_STATUS_PREPARED:
      return orderType === ORDER_TYPE_DELIVERY
        ? ORDER_STATUS_DELIVERING
        : ORDER_STATUS_COMPLETED;
    case ORDER_STATUS_DELIVERING:
      return ORDER_STATUS_COMPLETED;
    default:
      return null;
  }
};

// returns how long the given timestamp is ago (in seconds)
export const calculateAgoSeconds = (timestamp) =>
  (new Date(timestamp) - new Date()) / 1000;

export const calculateUpdateIntervalSeconds = (timestamp) =>
  calculateAgoSeconds(timestamp) < 60
    ? 1
    : calculateAgoSeconds(timestamp) < 60 * 60
    ? 60
    : 0;

export const calculateLighterAndDarker = (hexColor, intensity = 0.1) => {
  //intensity should be decimal number exmple : 0.1
  const color = hexColor.replace('#', '');
  const num = parseInt(color, 16);

  // Calculate lighter color
  const amount = intensity; // adjust this value to make the color lighter or darker
  const r = Math.min(255, parseInt((num >> 16) * (1 + amount), 10));
  const g = Math.min(255, parseInt(((num >> 8) & 0x00ff) * (1 + amount), 10));
  const b = Math.min(255, parseInt((num & 0x0000ff) * (1 + amount), 10));
  const lighterHex =
    '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');

  // Calculate darker color
  const amount2 = intensity; // adjust this value to make the color lighter or darker
  const r2 = Math.max(0, parseInt((num >> 16) * (1 - amount2), 10));
  const g2 = Math.max(0, parseInt(((num >> 8) & 0x00ff) * (1 - amount2), 10));
  const b2 = Math.max(0, parseInt((num & 0x0000ff) * (1 - amount2), 10));
  const darkerHex =
    '#' + ((r2 << 16) | (g2 << 8) | b2).toString(16).padStart(6, '0');

  return { lighter: lighterHex, darker: darkerHex };
};

export const calculateTextColor = (hexColor) => {
  const color = hexColor.replace('#', '');
  const num = parseInt(color, 16);

  // Calculate perceived brightness of the color
  const r = (num >> 16) & 0xff;
  const g = (num >> 8) & 0xff;
  const b = num & 0xff;
  const perceivedBrightness = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return white or black based on perceived brightness
  return perceivedBrightness > 0.5 ? '#000000' : '#FFFFFF';
};

export const parseLocaleNumber = (stringNumber, locale) => {
  // determines the thousands separator of the current locale (by trying out)
  const thousandSeparator = Intl.NumberFormat(locale)
    .format(11111)
    .replace(/\p{Number}/gu, '');

  // determines the decimal separator of the current locale (by trying out)
  const decimalSeparator = Intl.NumberFormat(locale)
    .format(1.1)
    .replace(/\p{Number}/gu, '');

  return parseFloat(
    stringNumber
      .replace(new RegExp('\\' + thousandSeparator, 'g'), '') // remove all thousand separators
      .replace(new RegExp('\\' + decimalSeparator), '.'), // and replace decimal separators with a dot
  );
};

export const createLocaleNumberParser = (locale) => {
  const formatter = new Intl.NumberFormat(locale);
  const thousandSeparator = formatter.format(11111).replace(/\p{Number}/gu, '');
  const decimalSeparator = formatter.format(1.1).replace(/\p{Number}/gu, '');

  // Precompile regex for performance
  const thousandSeparatorRegex = new RegExp('\\' + thousandSeparator, 'g');
  const decimalSeparatorRegex = new RegExp('\\' + decimalSeparator);

  return (stringNumber) => {
    // Check if stringNumber is a valid string
    if (typeof stringNumber !== 'string') {
      return NaN; // or handle this case based on your requirements
    }

    // Replace thousand separators and convert decimal separator to dot
    const cleanedString = stringNumber
      .replace(thousandSeparatorRegex, '')
      .replace(decimalSeparatorRegex, '.');

    // Parse the cleaned string into a number
    const parsedNumber = parseFloat(cleanedString);

    // Check if parsedNumber is a valid number
    if (!isNaN(parsedNumber)) {
      return parsedNumber;
    } else {
      return NaN; // or handle this case based on your requirements
    }
  };
};

export const filterDuplicates = (array) => {
  const seenIds = new Set();
  return array.filter((item) => {
    if (seenIds.has(item.id)) {
      return false; // Duplicate ID, exclude from the filtered array
    }
    seenIds.add(item.id);
    return true; // Unique ID, include in the filtered array
  });
};

export const hexToRGBA = (hex, opacity) => {
  hex = hex.replace('#', '');

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export const setColorVars = (store) => {
  // in case we are in sign up component
  const root = document.documentElement;
  const globalStyle = getComputedStyle(document.documentElement);
  const textColor = calculateTextColor(
    globalStyle.getPropertyValue('--primary'),
  );
  root.style.setProperty('--text-color', textColor);

  if (store) {
    store?.primaryColor &&
      root.style.setProperty('--primary', store?.primaryColor);
    store?.secondaryColor &&
      root.style.setProperty('--secondary', store?.secondaryColor);

    if (store?.primaryColor) {
      const primaryColorCalculated = calculateLighterAndDarker(
        store.primaryColor,
        0.1,
      );
      //const backgroundColorCalculated = calculateLighterAndDarker('#f8f8f8', 0.3);

      const textColor = calculateTextColor(store.primaryColor);
      const textColorCard = calculateTextColor('#ffffff');

      root.style.setProperty('--body-bg', '#f6f6f6');
      root.style.setProperty(
        '--primary-lighter',
        primaryColorCalculated?.lighter,
      );
      root.style.setProperty(
        '--primary-darker',
        primaryColorCalculated?.darker,
      );
      root.style.setProperty('--text-color', textColor);
      root.style.setProperty('--text-color-card', textColorCard);
      root.style.setProperty(
        '--primary-rgb',
        hexToRGBA(globalStyle.getPropertyValue('--primary'), 0.2),
      );
    } else {
      const primaryColorCalculated = calculateLighterAndDarker(
        globalStyle.getPropertyValue('--primary'),
        0.1,
      );
      root.style.setProperty(
        '--primary-lighter',
        primaryColorCalculated?.lighter,
      );
      root.style.setProperty(
        '--primary-darker',
        primaryColorCalculated?.darker,
      );
      const textColor = calculateTextColor(primaryColorCalculated?.darker);
      const textColorCard = calculateTextColor('#ffffff');
      root.style.setProperty('--text-color', textColor);
      root.style.setProperty('--text-color-card', textColorCard);
    }
  }
};

// creates a new kiosk session
export const initKioskSession = (kiosk, store) => {
  if (!kiosk || !store) {
    throw new Error('Cannot init Kiosk Session: kiosk or store missing');
  }
  const kioskSession = {
    kiosk: {
      id: kiosk.id,
      name: kiosk.name,
    },
    storeId: store.id,
  };

  localStorage.setItem('kioskSession', JSON.stringify(kioskSession));
  console.info(`Started session on Kiosk (id=${kiosk.id})`);
};

// either tableSession kioskSession
export const getSession = (key) => {
  const sessionJson = localStorage.getItem(key);

  if (sessionJson) {
    let session;
    try {
      session = JSON.parse(sessionJson);
    } catch (e) {
      localStorage.removeItem(key);
      console.error(`Deleted ${key} as it cannot be parsed: "${sessionJson}"`);
      return null;
    }

    return session;
  }

  return null;
};

// creates a new table session
export const initTableSession = (table, store) => {
  if (!table || !store) {
    throw new Error('Cannot init Table Session: table or store missing');
  }
  const tableSession = {
    table: {
      id: table.id,
      slug: table.slug,
      name: table.name,
    },
    storeId: store.id,
    lastActionAt: new Date(),
  };

  localStorage.setItem('tableSession', JSON.stringify(tableSession));
  console.info(`Started session on Table (id=${table.id})`);
};

export const updateTableSession = debounce(
  () => {
    const tableSession = getSession('tableSession');

    // update lastActionAt
    localStorage.setItem(
      'tableSession',
      JSON.stringify({
        ...tableSession,
        lastActionAt: new Date(),
      }),
    );

    console.debug('Updated TableSession lastActionAt to now');
  },
  3000,
  true,
);

export const calculateDistance = (point1, point2) => {
  const earthRadius = 6371; // Radius of the Earth in kilometers

  // Helper to normalize coordinates - handles both object {lat, lng} and string "lat,lng" formats
  const normalizeCoords = (point) => {
    if (!point) return { lat: null, lng: null };
    if (typeof point === 'string') {
      const [lat, lng] = point.split(',').map(Number);
      return { lat, lng };
    }
    return { lat: point.lat, lng: point.lng };
  };

  const p1 = normalizeCoords(point1);
  const p2 = normalizeCoords(point2);

  const toRadians = (degrees) => (degrees * Math.PI) / 180;

  const dLat = toRadians(p2.lat - p1.lat);
  const dLon = toRadians(p2.lng - p1.lng);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(p1.lat)) *
      Math.cos(toRadians(p2.lat)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return earthRadius * c;
};

// used to retry resolving a Promise
export const withRetry = (fn, retries = 25, interval = 2000) => {
  return new Promise((resolve, reject) => {
    fn()
      .then(resolve)
      .catch((err) => {
        setTimeout(() => {
          const statusCode = err?.response?.status;

          // we don't need to try it again if no retries left or statusCode=408,429 or >=500
          if (
            retries === 0 ||
            (statusCode &&
              !(statusCode === 408 || statusCode === 429 || statusCode >= 500))
          ) {
            reject(err);
          } else {
            console.error(
              `Retrying to send request (${retries} retries left) ...`,
            );
            withRetry(fn, retries - 1, interval).then(resolve, reject);
          }
        }, interval);
      });
  });
};

export const extractAddressInfo = (response) => {
  const { addressComponents } = response;
  const extractedAddress = {};

  for (const component of addressComponents) {
    const componentType = component.types[0];

    switch (componentType) {
      case 'route':
        extractedAddress.street = component?.longText;
        break;

      case 'locality':
        extractedAddress.city = component?.longText;
        break;

      case 'country':
        extractedAddress.country = component?.shortText;
        break;
      case 'street_number':
        extractedAddress.streetNumber = component?.shortText;
        break;
      case 'postal_code':
        extractedAddress.zip = component?.shortText;
        break;
      case 'administrative_area_level_1':
        extractedAddress.state = component?.shortText;
        break;

      default:
        break;
    }
  }
  return extractedAddress;
};

export const fetchLocation = async (placeId) => {
  return fetchPlaceDetails(placeId);
};

export const replaceColorInLottie = (
  lottieData,
  newPrimaryHex,
  newSecondaryHex = null,
  newAlt1Hex = null,
  newAlt2Hex = null,
) => {
  // Helper function to convert hex color to RGBA string array
  const hexToRgbaStringArray = (hex) => {
    if (hex.length === 4) hex += hex.slice(1, 3);
    const r = (parseInt(hex.slice(1, 3), 16) / 255).toFixed(12);
    const g = (parseInt(hex.slice(3, 5), 16) / 255).toFixed(12);
    const b = (parseInt(hex.slice(5, 7), 16) / 255).toFixed(12);
    return `[${r},${g},${b}`; // don't close array, there might be a 4th alpha parameter
  };

  let newLottieDataString = JSON.stringify(lottieData);
  const newHexStrings = [
    newPrimaryHex,
    newSecondaryHex,
    newAlt1Hex,
    newAlt2Hex,
  ];
  const oldColorStrings = [
    '[0.88888888,0.88888888,0.88888888', // don't close array strings, there might be a 4th alpha parameter
    '[0.86666667,0.86666667,0.86666667',
    '[0.93333333,0.93333333,0.93333333',
    '[0.9372549,0.9372549,0.9372549',
  ];

  newHexStrings.forEach((newHex, index) => {
    if (newHex) {
      // Replace occurrences of the original color with the new color
      newLottieDataString = newLottieDataString.replaceAll(
        oldColorStrings[index],
        hexToRgbaStringArray(newHex),
      );
    }
  });

  // Parse the modified string back to a JSON object
  return JSON.parse(newLottieDataString);
};

export const areDatesWithinMinutes = (startTime, endTime, minutes) => {
  const startTimestamp = new Date(startTime).getTime();
  const endTimestamp = new Date(endTime).getTime();
  const minutesInMillis = minutes * 60 * 1000;

  return startTimestamp + minutesInMillis >= endTimestamp;
};

export const roundDate = (date) => {
  const clonedDate = new Date(date);
  const milliseconds = clonedDate.getMilliseconds();
  clonedDate.setMilliseconds(0);

  if (milliseconds >= 500) {
    clonedDate.setSeconds(clonedDate.getSeconds() + 1);
  }

  return clonedDate;
};

// prepares OrderItems from Cart to be sent in a Request to Backend
export const convertCartToOrderItems = (cart) =>
  cart?.map((orderItem) => {
    return {
      ...orderItem,
      itemData: undefined,
      itemDataId: orderItem.itemData.id,
      additions: orderItem.additions?.map((orderItemAddition) => ({
        ...orderItemAddition,
        additionGroupEntryId: orderItemAddition.additionGroupEntry.id,
        itemAddition: undefined,
      })),
    };
  });

export const getOrderBody = (
  checkout,
  cart,
  table,
  merchantProfileId = undefined,
  kiosk = undefined,
) => {
  const isPayAndGo = checkout.checkoutMethod === CHECKOUT_METHOD_PAY_AND_GO;
  return {
    merchantProfileId,
    checkoutMethod: checkout.checkoutMethod,
    billingAddressId: checkout?.billingAddressId,
    cartComment: checkout.cartComment,
    tip: checkout.tip,
    discountCode: checkout.discountCode,
    ...(!isPayAndGo && { items: convertCartToOrderItems(cart) }),
    ...(table &&
      (checkout.orderType === ORDER_TYPE_INHOUSE ||
        checkout.orderType === ORDER_TYPE_SELF_SERVICE) && {
        tableId: table.id,
      }),
    // V2: use notificationMethod/notificationRecipient instead of pagerNumber/customerEmail
    // Only include if there's a recipient (WhatsApp is handled post-order via webhook)
    ...(checkout.notificationMethod && checkout.notificationRecipient && {
      notificationMethod: checkout.notificationMethod,
      notificationRecipient: checkout.notificationRecipient,
    }),
    // Always forward customer fields so the backend can enforce
    // OrderConfig.requiredCustomerDetails (e.g. for delivery orders where
    // name/tel can be required by configuration).
    customerName: checkout?.customerName,
    customerTel: checkout?.customerTel,
    customerRoomNumber: checkout?.customerRoomNumber,
    ...(kiosk &&
      (checkout.orderType === ORDER_TYPE_SELF_SERVICE ||
        checkout.orderType === ORDER_TYPE_PICKUP) && {
        kioskId: kiosk.id,
        tableId: checkout?.tableId,
        pickupRequestedAt: DateTime.now().plus({ minutes: 5 }),
      }),
    ...(checkout.orderType === ORDER_TYPE_PICKUP &&
      !kiosk && {
        pickupRequestedAt: checkout?.pickupRequestedAt || DateTime.now().plus({ minutes: 30 }).toISO(),
      }),
    ...((checkout.orderType === ORDER_TYPE_DELIVERY ||
      checkout.orderType === ORDER_TYPE_CATERING) && {
      deliveryAddressId: checkout.deliveryAddressId,
      deliveryRequestedAt: checkout?.deliveryRequestedAt,
    }),
    ...(checkout.orderType === ORDER_TYPE_CATERING && {
      guestQty: checkout?.guestQty,
    }),
    // V2: paymentMethodFutureUsage triggers PaymentIntent update with setup_future_usage
    ...(checkout.paymentMethodFutureUsage && {
      paymentMethodFutureUsage: checkout.paymentMethodFutureUsage,
    }),
  };
};

export const hasScroll = (containerSelector) => {
  const containerElement = document.querySelector(containerSelector);

  if (!containerElement) return false;

  return containerElement.scrollHeight > containerElement.clientHeight;
};

export const bulkActionTool = async () => {
  const scopeOptions = [
    'items/food',
    'item-data/food',
    'item-additions/food',
    'item-categories',
    'tables',
    'orders',
    'discount-codes',
    'table-reservations',
  ];

  // User Input and Validation
  let scope;
  while (!scope) {
    scope = window.prompt(
      `[SCOPE] Please select a scope:\n${scopeOptions.join('\n')}`,
    );
    if (scope === null) return; // User canceled prompt, abort execution

    if (!scopeOptions.includes(scope)) {
      window.alert('Invalid scope. Please choose from available options.');
      scope = undefined;
    }
  }

  let query;
  query = window.prompt(
    `[QUERY] (optional) Please insert a query. If empty, all Entities from Scope "${scope}" will be selected. You need to set a limit, otherwise only 25 entries will be processed. Examples:\n\n` +
      [
        [
          'All Items in the Root Category',
          '?join=categories&filter=categories.id||$isnull&limit=9999',
        ],
        [
          'All ItemData whose nameEN contains "Dessert"',
          '?filter=nameEN||$cont||Dessert&limit=9999',
        ],
        [
          'All ItemData whose taxInhouse is 7',
          '?filter=taxInhouse||$eq||7&limit=9999',
        ],
        [
          'ItemData created before 2024-01-01 AND taxInhouse is 7',
          '?s={"taxInhouse": 7, "createdAt": {"$lt": "2024-01-01"}}',
        ],
      ]
        .map((set) => set.join('\n'))
        .join('\n\n'),
  );
  if (query === null) return; // User canceled prompt, abort execution

  // Fetch Target Entities
  const targetEntitiesPath = scope + query || '';
  const targetEntities = await baseGetQuery(targetEntitiesPath)
    .then(({ data }) => data)
    .catch((err) => {
      window.alert(`Error: ${err}`);
      return [];
    });
  const targetEntityIds = targetEntities.map(({ id }) => id);

  // User Confirmation and Action Choice
  if (targetEntityIds.length === 0) {
    window.alert('No entities found for this query. Please retry.');
    return;
  }

  console.log(`Found ${targetEntityIds.length} target entities.`);

  const actionOptions = ['update', 'delete'];
  let action;
  while (!actionOptions.includes(action)) {
    action = window.prompt(
      `[ACTION] Please select an action:\n${actionOptions.join('\n')}`,
    );

    if (action === null) return; // User canceled prompt, abort execution

    if (!actionOptions.includes(action)) {
      window.alert('Invalid action. Please choose from available options.');
      action = undefined;
    }
  }

  // Update Entities
  let payload;
  if (action === 'update') {
    while (!payload) {
      payload = window.prompt('[PAYLOAD] Please insert your payload (JSON):');

      if (payload === null) return; // User canceled prompt, abort execution

      try {
        payload = JSON.parse(payload);
      } catch (error) {
        window.alert('Invalid JSON format. Please try again.');
        payload = undefined;
      }
    }
  }

  // Confirmation before Execution
  const confirmation = window.confirm(
    `Are you sure you want to ${action} ${targetEntityIds.length} entities? This action cannot be undone!`,
  );
  if (!confirmation) return; // User canceled prompt, abort execution

  // Loop for Bulk Actions
  let successfulOperations = 0;
  for (const id of targetEntityIds) {
    const actionPath = scope + `/${id}`;
    try {
      switch (action) {
        case 'update':
          await basePatchQuery(actionPath, payload);
          successfulOperations++;
          break;
        case 'delete':
          await baseDeleteQuery(actionPath);
          successfulOperations++;
          break;
      }
    } catch (error) {
      console.error(`Error performing ${action} on entity ${id}:`, error);
    }
  }

  // Final Success Message
  window.alert(
    `${successfulOperations} out of ${targetEntityIds.length} entities ${action}d successfully. Reload the Page to see the results!`,
  );
};

global.sbcli = { bulkActionTool };

/**
 * Recurring weekly windows, in the store's local wall clock.
 *
 * A window is `{ dow, start, end }`: `dow` is ISO-8601 (1 = Monday), times are
 * `HH:mm`, the end is exclusive, `start === end` means 24 hours and
 * `end < start` spills into the next day.
 *
 * These used to be ISO instants projected into "the current week"
 * (`transformToWeek`); that drifted by ±1h across DST. There is no projection
 * left: `now` is resolved to the store's wall clock instead.
 */
const MINUTES_PER_DAY = 1440;
const MINUTES_PER_WEEK = 7 * MINUTES_PER_DAY;

const parseTimeOfDay = (value) => {
  const [hours, minutes] = value.split(':').map(Number);
  return hours * 60 + minutes;
};

/** Arc length in minutes; 0 means a full circle (a 24h day). */
const durationMinutes = (window) => {
  const start = parseTimeOfDay(window.start);
  const end = parseTimeOfDay(window.end);
  const duration =
    (((end - start) % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  return duration === 0 ? MINUTES_PER_DAY : duration;
};

/** Projects a window onto the 0..10079 axis, splitting Sunday->Monday wraps. */
/**
 * True only for something we can read as a wall-clock window.
 *
 * Callers legitimately end up with holes in these arrays: `getActiveWindow`
 * returns `undefined` when a store is closed, and a condition without windows
 * contributes `[undefined]`. Treating those as windows crashed the cart merge
 * with "Cannot read properties of undefined (reading 'dow')".
 */
export const isServiceWindow = (window) =>
  !!window &&
  typeof window === 'object' &&
  Number.isInteger(window.dow) &&
  window.dow >= 1 &&
  window.dow <= 7 &&
  typeof window.start === 'string' &&
  typeof window.end === 'string';

const expandToWeekArcs = (window) => {
  const from = (window.dow - 1) * MINUTES_PER_DAY + parseTimeOfDay(window.start);
  const to = from + durationMinutes(window);
  return to <= MINUTES_PER_WEEK
    ? [{ from, to }]
    : [
        { from, to: MINUTES_PER_WEEK },
        { from: 0, to: to - MINUTES_PER_WEEK },
      ];
};

const positionOnWeek = (target, timeZone) => {
  const local = DateTime.fromJSDate(target).setZone(timeZone);
  const minuteOfDay =
    local.hour * 60 + local.minute + local.second / 60 + local.millisecond / 60000;
  return (local.weekday - 1) * MINUTES_PER_DAY + minuteOfDay;
};

/** True when the store-local wall clock at `target` falls inside a window. */
export const isOpenAt = (windows, target, timeZone) => {
  if (!windows?.length) return false;
  const position = positionOnWeek(target, timeZone);
  return windows.filter(isServiceWindow).some((window) =>
    expandToWeekArcs(window).some(
      ({ from, to }) => position >= from && position < to,
    ),
  );
};

/** The window covering `target`, if any. */
export const getActiveWindow = (windows, target, timeZone) =>
  !windows?.length
    ? undefined
    : windows.filter(isServiceWindow).find((window) => {
        const position = positionOnWeek(target, timeZone);
        return expandToWeekArcs(window).some(
          ({ from, to }) => position >= from && position < to,
        );
      });

/** Materializes a window occurrence into real instants. */
export const getWindowBounds = (window, target, timeZone) => {
  if (!isServiceWindow(window)) return undefined;
  const startMinutes = parseTimeOfDay(window.start);
  const endMinutes = parseTimeOfDay(window.end);
  const crossesMidnight = endMinutes <= startMinutes;
  const local = DateTime.fromJSDate(target).setZone(timeZone);

  for (const weekOffset of [-1, 0, 1]) {
    const from = local
      .startOf('week')
      .plus({ weeks: weekOffset, days: window.dow - 1 })
      .set({ hour: Math.floor(startMinutes / 60), minute: startMinutes % 60 })
      .set({ second: 0, millisecond: 0 });
    const to = (crossesMidnight ? from.plus({ days: 1 }) : from).set({
      hour: Math.floor(endMinutes / 60),
      minute: endMinutes % 60,
    });

    if (from.toMillis() <= target.getTime() && target.getTime() < to.toMillis()) {
      return { startAt: from.toJSDate(), endAt: to.toJSDate() };
    }
  }

  return undefined;
};

/** Backwards-compatible wrapper: the active window with real instants. */
export const getCurrentAtDate = (
  windows,
  targetDate = new Date(),
  timeZone = 'Europe/Berlin',
) => {
  const active = getActiveWindow(windows, targetDate, timeZone);
  return active ? getWindowBounds(active, targetDate, timeZone) : undefined;
};

/** Next window start strictly after `targetDate`. */
export const getNextStart = (
  windows,
  targetDate = new Date(),
  timeZone = 'Europe/Berlin',
) => {
  if (!windows?.length) return undefined;

  const local = DateTime.fromJSDate(targetDate).setZone(timeZone);
  let best;

  for (const window of windows.filter(isServiceWindow)) {
    const startMinutes = parseTimeOfDay(window.start);
    for (const weekOffset of [-1, 0, 1]) {
      const candidate = local
        .startOf('week')
        .plus({ weeks: weekOffset, days: window.dow - 1 })
        .set({ hour: Math.floor(startMinutes / 60), minute: startMinutes % 60 })
        .set({ second: 0, millisecond: 0 });

      if (
        candidate.toMillis() > targetDate.getTime() &&
        (!best || candidate.toMillis() < best.startAt.getTime())
      ) {
        best = { startAt: candidate.toJSDate(), ...window };
      }
    }
  }

  return best;
};

/*const getDay = (
  serviceTimes,
  timeZone = 'Europe/Berlin',
  dateInTargetDay = new Date(),
) => {
  const startOfDay = DateTime.fromJSDate(dateInTargetDay)
    .setZone(timeZone)
    .startOf('day');
  const endOfDay = startOfDay.endOf('day');
  const targetServiceTimes = serviceTimes?.filter(({ startAt, endAt }) => {
    const start = DateTime.fromJSDate(startAt);
    const end = DateTime.fromJSDate(endAt);
    return (
      (startOfDay <= start && endOfDay >= start) ||
      (startOfDay <= end && endOfDay >= end)
    );
  });

  if (!targetServiceTimes) throw new Error('Cannot find suitable ServiceTimes');

  return targetServiceTimes;
};*/

export const isOrderConditionSatisfied = (
  orderCondition,
  orderClass = undefined,
  total = undefined,
  qty = undefined,
  targetDate = new Date(),
) => {
  const isServiceTimeSatisfied =
    !orderCondition.serviceWindows?.length ||
    isOpenAt(
      orderCondition.serviceWindows,
      targetDate,
      orderCondition.store?.timeZone || 'Europe/Berlin',
    ); // no service windows = always active
  const isOrderClassSatisfied =
    !orderClass ||
    !orderCondition.classes?.length || // no classes set = available for all classes
    orderCondition.classes.includes(orderClass);
  const isMinTotalSatisfied =
    !isNumber(total) ||
    orderCondition.minTotal === null ||
    orderCondition.minTotal === undefined ||
    orderCondition.minTotal <= total;
  const isMaxTotalSatisfied =
    !isNumber(total) ||
    orderCondition.maxTotal === null ||
    orderCondition.maxTotal === undefined ||
    orderCondition.maxTotal >= total;
  const isMinQtySatisfied =
    !isNumber(qty) ||
    orderCondition.minQty === null ||
    orderCondition.minQty === undefined ||
    orderCondition.minQty <= qty;
  const isMaxQtySatisfied =
    !isNumber(qty) ||
    orderCondition.maxQty === null ||
    orderCondition.maxQty === undefined ||
    orderCondition.maxQty >= qty;

  return (
    orderCondition.isActive &&
    isServiceTimeSatisfied &&
    isOrderClassSatisfied &&
    isMinTotalSatisfied &&
    isMaxTotalSatisfied &&
    isMinQtySatisfied &&
    isMaxQtySatisfied
  );
};

export const hasSatisfiedOrderCondition = (
  orderConditions,
  orderType,
  total,
  qty,
  targetDate = new Date(),
) => {
  // No conditions = always available. A set of exclusively inactive conditions
  // is also treated as available (an inactive condition imposes no constraint).
  // This matches the kiosk client (sb-kiosk order-conditions.ts). NOTE: the
  // backend currently treats an all-inactive set as NOT satisfied; see the
  // OrderCondition notes in AGENTS.md for that separate discrepancy.
  if (!orderConditions?.length) return true;
  const allInactive = orderConditions.every(
    (orderCondition) => !orderCondition?.isActive,
  );
  if (allInactive) return true;

  return !!getSatisfiedOrderCondition(
    orderConditions,
    getOrderClass(orderType),
    total,
    qty,
    targetDate,
  );
};

/**
 * Like hasSatisfiedOrderCondition, but ignores whether the OrderCondition
 * service time is active *right now*. Used for pre-order flows
 * (pickup/delivery/catering) where the customer chooses a future fulfillment
 * time: the item should be orderable as long as it has an OrderCondition that
 * matches the order class and qty limits for some upcoming time (service times
 * recur weekly). The exact requested time is validated separately against the
 * OrderCondition service times AND the store service times when the
 * pickup/delivery time is chosen.
 *
 * Items without OrderConditions (or with only inactive ones) are always
 * orderable.
 */
export const hasUpcomingSatisfiedOrderCondition = (
  orderConditions,
  orderType,
  total,
  qty,
) => {
  if (!orderConditions?.length) return true;

  const activeConditions = orderConditions.filter(
    (orderCondition) => orderCondition?.isActive,
  );
  if (!activeConditions.length) return true; // only inactive = always available

  const orderClass = getOrderClass(orderType);

  return activeConditions.some(
    (orderCondition) =>
      (!orderClass ||
        !orderCondition.classes?.length || // empty classes = available for all classes
        orderCondition.classes.includes(orderClass)) &&
      (!isNumber(qty) || orderCondition.minQty <= qty) &&
      (!isNumber(qty) ||
        orderCondition.maxQty === null ||
        orderCondition.maxQty === undefined ||
        orderCondition.maxQty >= qty),
  );
};

/**
 * The fulfillment time the order must be evaluated against: the chosen
 * pickup/delivery time for pre-order types, undefined otherwise (meaning
 * "right now").
 *
 * Only pre-order types may carry a requested time. A customer who picks a
 * pickup time and then switches to inhouse leaves a stale
 * `checkout.pickupRequestedAt` behind, and using it would evaluate item
 * availability against a time the order will never be fulfilled at.
 */
export const getEffectiveRequestedAt = (checkout) => {
  if (!isTakeawayOrderClass(checkout?.orderType)) return undefined;
  const requestedAt = checkout.pickupRequestedAt || checkout.deliveryRequestedAt;
  const date = requestedAt ? new Date(requestedAt) : null;
  return date && !Number.isNaN(date.getTime()) ? date : undefined;
};

/**
 * Availability check for an OrderCondition scope (item data, addition group,
 * entry or addition) when the customer is browsing/placing an order.
 *
 * - Fulfillment time already chosen: the scope's service-time window must
 *   contain that moment.
 * - Pre-order type (pickup/delivery/catering) with no time chosen yet: the
 *   window must cover some upcoming moment (service times recur weekly). The
 *   exact time is validated when it is chosen and again on the backend, which
 *   evaluates against `pickupRequestedAt ?? deliveryRequestedAt ?? now`.
 * - Immediate type (inhouse/self-service): there is no future to plan for, so
 *   the window must contain now.
 *
 * @param {Array} orderConditions
 * @param {string} orderType
 * @param {Date} [requestedAt] - Chosen fulfillment time, if any.
 */
export const isOrderConditionScopeAvailable = (
  orderConditions,
  orderType,
  requestedAt,
) => {
  if (requestedAt) {
    return hasSatisfiedOrderCondition(
      orderConditions,
      orderType,
      undefined,
      undefined,
      requestedAt,
    );
  }
  return isTakeawayOrderClass(orderType)
    ? hasUpcomingSatisfiedOrderCondition(orderConditions, orderType)
    : hasSatisfiedOrderCondition(orderConditions, orderType);
};

/**
 * Like isOrderConditionScopeAvailable, but returns the applicable
 * OrderCondition itself so the caller can read its qty/total limits.
 *
 * For a pre-order that has no fulfillment time chosen yet the service-time
 * window is ignored (an upcoming window still qualifies), so a group that is
 * shown to the customer also has its min/max constraints applied instead of
 * silently resolving to "no condition".
 */
export const getApplicableOrderCondition = (
  orderConditions,
  orderType,
  requestedAt,
) => {
  const orderClass = getOrderClass(orderType);
  if (requestedAt || !isTakeawayOrderClass(orderType)) {
    return getSatisfiedOrderCondition(
      orderConditions,
      orderClass,
      undefined,
      undefined,
      requestedAt ?? new Date(),
    );
  }

  return orderConditions
    ?.filter((orderCondition) => orderCondition?.isActive)
    .sort((a, b) => b.priority - a.priority)
    .find(
      (orderCondition) =>
        !orderClass ||
        !orderCondition.classes?.length || // empty classes = all classes
        orderCondition.classes.includes(orderClass),
    );
};

export const getSatisfiedOrderCondition = (
  orderConditions,
  orderClass = null,
  total = null,
  qty = null,
  targetDate = new Date(),
) =>
  orderConditions
    ?.sort((a, b) => b.priority - a.priority) // sort DESC by priority (high priority first)
    .find((orderCondition) =>
      isOrderConditionSatisfied(
        orderCondition,
        orderClass,
        total,
        qty,
        targetDate,
      ),
    );

// Builds the list of violated min/max qty & total constraints for a single
// applicable OrderCondition.
const collectConditionViolations = (orderCondition, qty, total) => {
  const violations = [];
  if (
    orderCondition.minQty !== null &&
    orderCondition.minQty !== undefined &&
    qty < orderCondition.minQty
  ) {
    violations.push({ type: 'minQty', limit: orderCondition.minQty, current: qty });
  }
  if (
    orderCondition.maxQty !== null &&
    orderCondition.maxQty !== undefined &&
    qty > orderCondition.maxQty
  ) {
    violations.push({ type: 'maxQty', limit: orderCondition.maxQty, current: qty });
  }
  if (
    orderCondition.minTotal !== null &&
    orderCondition.minTotal !== undefined &&
    total < orderCondition.minTotal
  ) {
    violations.push({ type: 'minTotal', limit: orderCondition.minTotal, current: total });
  }
  if (
    orderCondition.maxTotal !== null &&
    orderCondition.maxTotal !== undefined &&
    total > orderCondition.maxTotal
  ) {
    violations.push({ type: 'maxTotal', limit: orderCondition.maxTotal, current: total });
  }
  return violations;
};

// Validates qty AND total against a set of OrderConditions using the backend's
// ItemData semantics (OrderBaseService.checkItemDataOrderConditions ->
// OrderConditionService.hasSatisfiedOrderCondition): the scope is valid when at
// least one active condition is fully satisfied (class + service time + qty +
// total). Returns { isValid, violations } so the UI can explain the breach.
export const validateOrderConditions = (
  orderConditions,
  orderClass,
  qty,
  total,
  targetDate = new Date(),
) => {
  if (!orderConditions?.length) {
    return { isValid: true, violations: [] };
  }

  // All inactive = no constraint = valid (matches the kiosk client). See the
  // all-inactive note on hasSatisfiedOrderCondition.
  const activeConditions = orderConditions.filter((c) => c.isActive);
  if (activeConditions.length === 0) {
    return { isValid: true, violations: [] };
  }

  const isFullySatisfied = activeConditions.some((c) =>
    isOrderConditionSatisfied(c, orderClass, total, qty, targetDate),
  );
  if (isFullySatisfied) {
    return { isValid: true, violations: [] };
  }

  // Pick the highest-priority active condition applicable for the current class
  // & service time (ignoring qty/total) to explain which bounds were breached.
  const applicable = [...activeConditions]
    .sort((a, b) => b.priority - a.priority)
    .find((c) =>
      isOrderConditionSatisfied(c, orderClass, undefined, undefined, targetDate),
    );

  if (applicable) {
    return { isValid: false, violations: collectConditionViolations(applicable, qty, total) };
  }

  // Nothing applies at targetDate. Distinguish WHY, because a pre-order can
  // resolve a time problem by choosing a different fulfillment time but can
  // never resolve an order-class problem:
  // - classMismatch: no active condition covers this order class at all.
  // - serviceTimeMismatch: some condition covers the class, but no service
  //   time window contains targetDate.
  // When the class fits and only the time is wrong, the bounds of that
  // condition are reported on top, so a pre-order that ignores the time
  // violation still cannot exceed an upper bound.
  const byClass = [...activeConditions]
    .sort((a, b) => b.priority - a.priority)
    .find(
      (c) =>
        !orderClass ||
        !c.classes?.length || // empty classes = available for all classes
        c.classes.includes(orderClass),
    );

  if (!byClass) {
    return { isValid: false, violations: [{ type: 'classMismatch', current: qty }] };
  }

  return {
    isValid: false,
    violations: [
      { type: 'serviceTimeMismatch', current: qty },
      ...collectConditionViolations(byClass, qty, total),
    ],
  };
};

// Validates qty AND total against a set of OrderConditions using the backend's
// single highest-priority semantics (OrderBaseService.isWithinConditionLimits,
// used for the ItemAddition whole-order check and the addition group/entry
// checks): select the highest-priority condition applicable for the order class
// & service time (qty/total ignored), then check qty/total against ONLY that
// condition. If none applies, the scope is unconstrained (valid).
export const validateConditionLimits = (
  orderConditions,
  orderClass,
  qty,
  total,
  targetDate = new Date(),
) => {
  if (!orderConditions?.length) {
    return { isValid: true, violations: [] };
  }

  const applicable = getSatisfiedOrderCondition(
    orderConditions,
    orderClass,
    undefined,
    undefined,
    targetDate,
  );

  if (!applicable) {
    return { isValid: true, violations: [] };
  }

  const violations = collectConditionViolations(applicable, qty, total);
  return { isValid: violations.length === 0, violations };
};

// Aggregates the cart and validates every OrderCondition the backend enforces
// at order creation (OrderBaseService.checkItemDataOrderConditions,
// checkAdditionOrderConditionsOrderWide and checkAdditionOrderConditions),
// using the exact same comparison semantics. Returns an array of violation
// descriptors; an empty array means the cart would pass the backend checks.
//
// This is the authoritative client-side guard that prevents the backend from
// ever returning *_ORDER_CONDITION_NOT_SATISFIED for a cart the UI accepted.
export const getCartOrderConditionViolations = (
  cart,
  orderType,
  targetDate = new Date(),
) => {
  const items = cart || [];
  if (!items.length) return [];

  const orderClass = getOrderClass(orderType);
  const violations = [];

  // --- ItemData level: aggregate qty + base-price total per ItemData ---
  const itemDataAggregates = new Map();
  for (const orderItem of items) {
    const itemData = orderItem.itemData;
    if (!itemData?.orderConditions?.length) continue;
    const price = getItemDataOrAdditionPrice(orderType, itemData);
    const existing = itemDataAggregates.get(itemData.id);
    if (existing) {
      existing.qty += orderItem.qty;
      existing.total += orderItem.qty * price;
    } else {
      itemDataAggregates.set(itemData.id, {
        qty: orderItem.qty,
        total: orderItem.qty * price,
        itemData,
      });
    }
  }
  for (const { qty, total, itemData } of itemDataAggregates.values()) {
    const result = validateOrderConditions(
      itemData.orderConditions,
      orderClass,
      qty,
      total,
      targetDate,
    );
    if (!result.isValid) {
      violations.push({
        scope: 'ITEM_DATA',
        id: itemData.id,
        name: getLocalizedName(itemData),
        violations: result.violations,
      });
    }
  }

  // --- ItemAddition level: aggregate qty + paid total per addition, order-wide.
  // Free-of-charge is allocated per (orderItem, group) in addition order so the
  // paid total matches the backend exactly. ---
  const additionAggregates = new Map();
  for (const orderItem of items) {
    const remainingFree = new Map();
    for (const oia of orderItem.additions ?? []) {
      const entry = oia.additionGroupEntry;
      const addition = entry?.addition;
      const groupId = entry?.group?.id;

      // Replicate the free-of-charge pool consumption for every addition,
      // regardless of whether it carries conditions.
      if (groupId !== undefined && !remainingFree.has(groupId)) {
        remainingFree.set(
          groupId,
          entry?.group?.freeOfChargeSelections ?? 0,
        );
      }
      const free = groupId !== undefined ? remainingFree.get(groupId) ?? 0 : 0;
      let freeOfChargeQty;
      if (oia.qty >= free) {
        freeOfChargeQty = free;
        if (groupId !== undefined) remainingFree.set(groupId, 0);
      } else {
        freeOfChargeQty = oia.qty;
        if (groupId !== undefined) remainingFree.set(groupId, free - oia.qty);
      }

      if (!addition?.orderConditions?.length) continue;

      const price = getItemDataOrAdditionPrice(orderType, addition);
      const qty = orderItem.qty * oia.qty;
      const total = orderItem.qty * (oia.qty - freeOfChargeQty) * price;
      const existing = additionAggregates.get(addition.id);
      if (existing) {
        existing.qty += qty;
        existing.total += total;
      } else {
        additionAggregates.set(addition.id, { qty, total, addition });
      }
    }
  }
  for (const { qty, total, addition } of additionAggregates.values()) {
    const result = validateConditionLimits(
      addition.orderConditions,
      orderClass,
      qty,
      total,
      targetDate,
    );
    if (!result.isValid) {
      violations.push({
        scope: 'ITEM_ADDITION',
        id: addition.id,
        name: getLocalizedName(addition),
        violations: result.violations,
      });
    }
  }

  // --- ItemAdditionGroup + ItemAdditionGroupEntry level: per order item ---
  for (const orderItem of items) {
    const orderItemAdditions = orderItem.additions ?? [];

    // All groups of the item (when available) so mandatory groups (minQty > 0)
    // are enforced even when nothing was selected; fall back to the groups
    // referenced by the current selections.
    const allGroups =
      orderItem.itemData?.additionGroups ??
      [
        ...new Map(
          orderItemAdditions
            .map((oia) => oia.additionGroupEntry?.group)
            .filter((group) => group?.id)
            .map((group) => [group.id, group]),
        ).values(),
      ];

    const paidTotalForGroup = (groupId) => {
      const groupAdditions = orderItemAdditions.filter(
        (oia) => oia.additionGroupEntry?.group?.id === groupId,
      );
      const freeOfChargeSelections =
        groupAdditions[0]?.additionGroupEntry?.group?.freeOfChargeSelections ||
        0;
      let currentQty = 1;
      let total = 0;
      let qty = 0;
      for (const oia of groupAdditions) {
        const price = getItemDataOrAdditionPrice(
          orderType,
          oia.additionGroupEntry?.addition,
        );
        qty += oia.qty;
        for (let i = 0; i < oia.qty; i++) {
          if (currentQty > freeOfChargeSelections) total += price;
          currentQty++;
        }
      }
      return { qty, total };
    };

    for (const group of allGroups) {
      if (!group?.orderConditions?.length) continue;
      const { qty, total } = paidTotalForGroup(group.id);
      const result = validateConditionLimits(
        group.orderConditions,
        orderClass,
        qty,
        total,
        targetDate,
      );
      if (!result.isValid) {
        violations.push({
          scope: 'ITEM_ADDITION_GROUP',
          id: group.id,
          name: getLocalizedName(group),
          violations: result.violations,
        });
      }
    }

    // Entry level: aggregate per selected entry within this item.
    const entryAggregates = new Map();
    for (const oia of orderItemAdditions) {
      const entry = oia.additionGroupEntry;
      if (!entry?.orderConditions?.length) continue;
      const price = getItemDataOrAdditionPrice(orderType, entry.addition);
      const existing = entryAggregates.get(entry.id);
      const total = oia.qty * price;
      if (existing) {
        existing.qty += oia.qty;
        existing.total += total;
      } else {
        entryAggregates.set(entry.id, { qty: oia.qty, total, entry });
      }
    }
    for (const { qty, total, entry } of entryAggregates.values()) {
      const result = validateConditionLimits(
        entry.orderConditions,
        orderClass,
        qty,
        total,
        targetDate,
      );
      if (!result.isValid) {
        violations.push({
          scope: 'ITEM_ADDITION_GROUP_ENTRY',
          id: entry.id,
          name: getLocalizedName(entry.addition),
          violations: result.violations,
        });
      }
    }
  }

  return violations;
};

/**
 * Verifies that every OrderCondition scope in the cart (item data, addition,
 * group and entry level) is *available* at the given target time — active, with
 * a matching order class and a service-time window covering that time.
 *
 * Quantity/total limits are deliberately ignored here: they are enforced at add
 * time (upper bounds) and in handleCreateOrder (both bounds), because a min
 * bound must not make an item look unavailable while the cart grows toward it.
 *
 * @param {Array} cart - The cart (array of OrderItems with itemData + qty).
 * @param {string} orderType - The order type (used for order class gating).
 * @param {Date} [targetDate] - The time to validate against (e.g. pickupRequestedAt).
 * @returns {boolean} True if every scope is available at targetDate.
 */
export const isCartOrderConditionsSatisfiedAtDate = (
  cart,
  orderType,
  targetDate = new Date(),
) =>
  getCartOrderConditionViolations(cart, orderType, targetDate)
    .flatMap(({ violations }) => violations)
    .every(
      ({ type }) => type !== 'classMismatch' && type !== 'serviceTimeMismatch',
    );

export const mergeOrderConditions = (
  orderConditions,
  targetDate = new Date(), // now as default
) =>
  orderConditions?.length
    ? orderConditions.reduce((result, current) => ({
        isActive: result.isActive || current.isActive,
        classes: result.classes.filter((type) =>
          current.classes.includes(type),
        ), // include all classes that are already known (=intersection)
        priority: Math.max(result.priority, current.priority),
        minQty: Math.max(result.minQty, current.minQty),
        maxQty: Math.min(result.maxQty, current.maxQty),
        minTotal: Math.max(result.minTotal, current.minTotal),
        maxTotal: Math.min(result.maxTotal, current.maxTotal),
        serviceWindows: intersectServiceWindows(
          [result, current].map((condition) => {
            const active = getActiveWindow(
              condition.serviceWindows,
              targetDate,
              condition.store?.timeZone || 'Europe/Berlin',
            );
            return active ? [active] : undefined;
          }),
        ),
      }))
    : null;

const formatTimeOfDay = (minutes) => {
  const normalized =
    ((minutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const hours = String(Math.floor(normalized / 60)).padStart(2, '0');
  const mins = String(normalized % 60).padStart(2, '0');
  return `${hours}:${mins}`;
};

/**
 * Intersection of several window lists: only the minutes open in all of them.
 * An empty/undefined list imposes no restriction and is skipped, matching the
 * previous `mergeServiceTimes` behaviour on order conditions.
 */
export const intersectServiceWindows = (...lists) => {
  const defined = lists
    .filter((list) => Array.isArray(list))
    .map((list) => list.filter(isServiceWindow))
    .filter((list) => list.length);
  if (!defined.length) return [];

  const arcs = defined.map((list) => list.flatMap(expandToWeekArcs));
  const intersected = arcs.reduce((a, b) =>
    a.flatMap((x) =>
      b
        .map((y) => ({
          from: Math.max(x.from, y.from),
          to: Math.min(x.to, y.to),
        }))
        .filter(({ from, to }) => from < to),
    ),
  );

  return intersected.map(({ from, to }) => ({
    dow: Math.floor(from / MINUTES_PER_DAY) + 1,
    start: formatTimeOfDay(from % MINUTES_PER_DAY),
    end: formatTimeOfDay((from % MINUTES_PER_DAY) + (to - from)),
  }));
};


// generates a unique hash for itemData + additionGroupEntry combinations
// to be able to identify, if this item is already in the cart
export const getOrderItemHash = ({ itemData, additions }) => {
  const ids =
    itemData?.id +
    [...(additions || [])]
      .sort((a, b) =>
        a.additionGroupEntry?.id.localeCompare(b.additionGroupEntry?.id),
      )
      .reduce(
        (hash, { qty, additionGroupEntry }) =>
          hash + '+' + qty + additionGroupEntry.id,
        '',
      );

  return createHash(ids);
};

// returns the sum of all quantities in an array of OrderItems (= cart) for an ItemData
export const getItemDataQtySum = (orderItems, itemData) =>
  (itemData &&
    orderItems?.reduce(
      (sum, { id, qty }) => sum + (id === itemData.id ? qty : 0),
      0,
    )) ||
  NaN;

// returns the sum of all totals in an array of OrderItems (= cart) for an ItemData
export const getItemDataTotalSum = (orderItems, itemData, orderType) =>
  getItemDataQtySum(orderItems, itemData) *
  getItemDataOrAdditionPrice(orderType, itemData);

// returns a subset of the given OrderItems, while each OrderItem contains only the given ItemAddition
export const getOrderItemsContainingItemAddition = (orderItems, itemAddition) =>
  itemAddition &&
  (orderItems || [])
    .filter(({ additions }) =>
      (additions || []).some(
        ({ additionGroupEntry }) =>
          additionGroupEntry?.addition?.id === itemAddition.id,
      ),
    )
    .map((orderItem) => ({
      ...orderItem,
      additions: orderItem.additions.filter(
        ({ additionGroupEntry }) =>
          additionGroupEntry?.addition?.id === itemAddition.id,
      ),
    }));

// returns the sum of all quantities in an array of OrderItems (= cart) for an ItemAddition
export const getItemAdditionQtySum = (cart, itemAddition) => {
  return getOrderItemsContainingItemAddition(cart, itemAddition).reduce(
    (sum, { qty, additions }) =>
      sum + qty * additions.reduce((sum, { qty }) => sum + qty, 0),
    0,
  );
};

// returns the sum of all totals in an array of OrderItems (= cart) for an ItemAddition
export const getItemAdditionsTotalSum = (cart, itemAddition, orderType) =>
  getItemAdditionQtySum(cart, itemAddition) *
  getItemDataOrAdditionPrice(orderType, itemAddition);

// returns an array of available ItemAdditionGroups, including their entries
// `requestedAt` (optional) is the chosen fulfillment time of a pre-order; when
// absent, pre-order types fall back to "available at some upcoming time" and
// immediate types to "available now" (see isOrderConditionScopeAvailable).
export const getAvailableItemAdditionGroups = (
  additionGroups,
  orderType,
  posIntegration = null,
  requestedAt = undefined,
) =>
  [...(additionGroups || [])]
    // check OrderConditions on ItemAdditionGroup level
    .filter(
      ({ orderConditions }) =>
        isOrderConditionScopeAvailable(orderConditions, orderType, requestedAt),
    )
    .map((additionGroup) => ({
      ...additionGroup,
      entries: (additionGroup.entries || [])
        // check posIntegration - only POS types that actually map additions
        // need a ref (ready2order folds additions into the item, so its
        // additions never have one and must stay visible)
        .filter(
          ({ addition }) =>
            !posIntegrationRequiresAdditionMapping(posIntegration) ||
            !!addition?.posIntegrationRef,
        )
        // check OrderConditions on ItemAdditionGroupEntry level
        .filter(({ orderConditions }) =>
          isOrderConditionScopeAvailable(
            orderConditions,
            orderType,
            requestedAt,
          ),
        )
        // check OrderConditions on ItemAddition level
        .filter(({ addition }) =>
          isOrderConditionScopeAvailable(
            addition?.orderConditions,
            orderType,
            requestedAt,
          ),
        )
        // sort by manualRanking (ItemAdditionGroupEntry level)
        .sort((a, b) => a.manualRanking - b.manualRanking),
    }))
    // check if there are any entries left
    .filter(({ entries }) => entries?.length > 0)
    // sort by manualRanking (ItemAdditionGroup level)
    .sort((a, b) => a.manualRanking - b.manualRanking);

// returns the price of an ItemData + the prices of all pre-selected ItemAdditionEntries
export const getItemDataPriceWithPreSelections = (
  orderType,
  itemData,
  posIntegration = null,
  requestedAt = undefined,
) => {
  // these are the pre-selected ItemAdditionGroupEntries, packed in OrderItemAdditions
  const additions = getAvailableItemAdditionGroups(
    itemData?.additionGroups,
    orderType,
    posIntegration,
    requestedAt,
  ).flatMap((group) =>
    group.entries
      .filter(({ isPreSelected }) => isPreSelected)
      .map((entry) => ({
        qty: 1, // currently it's only possible to pre-select qty=1
        additionGroupEntry: {
          ...entry,
          group: { ...group, entries: undefined }, // exclude entries to avoid circular structure
        },
      })),
  );

  return getOrderItemTotal(orderType, { itemData, additions }, true);
};

// returns true if given value is a number and not NaN
export const isNumber = (value) =>
  typeof value === 'number' && !Number.isNaN(value);

// returns true if given orderType is valid
export const isOrderTypeValid = (orderType) =>
  [
    ORDER_TYPE_INHOUSE,
    ORDER_TYPE_SELF_SERVICE,
    ORDER_TYPE_PICKUP,
    ORDER_TYPE_DELIVERY,
    ORDER_TYPE_CATERING,
  ].includes(orderType);

// Maps order type to the corresponding order config property name on the store
export const ORDER_TYPE_TO_CONFIG_KEY = {
  [ORDER_TYPE_INHOUSE]: 'inhouseOrderConfig',
  [ORDER_TYPE_PICKUP]: 'pickupOrderConfig',
  [ORDER_TYPE_DELIVERY]: 'deliveryOrderConfig',
  [ORDER_TYPE_CATERING]: 'cateringOrderConfig',
  [ORDER_TYPE_SELF_SERVICE]: 'selfServiceOrderConfig',
};

// Returns the order config for a given order type from the store
export const getOrderConfig = (store, orderType) => {
  if (!store || !orderType) return null;
  const configKey = ORDER_TYPE_TO_CONFIG_KEY[orderType];
  return configKey ? store[configKey] : null;
};

// Returns true if all enabled order configs have a posIntegration.
// When true, items without posIntegrationRef cannot be ordered on any order type.
export const allEnabledConfigsHavePosIntegration = (store) => {
  if (!store) return false;
  const enabledConfigs = Object.values(ORDER_TYPE_TO_CONFIG_KEY)
    .map((key) => store[key])
    .filter((config) => config?.isEnabled);
  return (
    enabledConfigs.length > 0 &&
    enabledConfigs.every((config) => !!config.posIntegration)
  );
};

// Returns true if any order config on the store has a posIntegration.
// Used by admin forms to decide whether to show posIntegrationRef fields.
export const anyConfigHasPosIntegration = (store) => {
  if (!store) return false;
  return Object.values(ORDER_TYPE_TO_CONFIG_KEY).some(
    (key) => !!store[key]?.posIntegration,
  );
};

// Whether a PosIntegration requires every ItemAddition to carry a
// posIntegrationRef to be orderable. Some POS types (ready2order) have no
// additions concept at all - additions are folded into the item - so their
// additions never get a ref and must NOT be hidden.
// The backend sends `requiresAdditionMapping` on public payloads; fall back to
// the type for manager payloads / older responses.
export const posIntegrationRequiresAdditionMapping = (posIntegration) => {
  if (!posIntegration) return false;
  if (typeof posIntegration.requiresAdditionMapping === 'boolean') {
    return posIntegration.requiresAdditionMapping;
  }
  return posIntegration.type !== 'Ready2OrderPosIntegration';
};

// Same as anyConfigHasPosIntegration, but only true when that POS actually
// requires additions to be mapped (see above).
export const anyConfigRequiresAdditionMapping = (store) => {
  if (!store) return false;
  return Object.values(ORDER_TYPE_TO_CONFIG_KEY).some((key) =>
    posIntegrationRequiresAdditionMapping(store[key]?.posIntegration),
  );
};

// Returns the PosIntegration configured on any of the store's order configs
// (or null). All configs of a store reference the same PosIntegration, so the
// first one found is sufficient to fetch POS mappings.
export const getConfigPosIntegration = (store) => {
  if (!store) return null;
  for (const key of Object.values(ORDER_TYPE_TO_CONFIG_KEY)) {
    if (store[key]?.posIntegration) return store[key].posIntegration;
  }
  return null;
};

// Order type to translation message ID mapping
const ORDER_TYPE_TO_LABEL_ID = {
  [ORDER_TYPE_INHOUSE]: 'orderTypeInhouseOrder',
  [ORDER_TYPE_PICKUP]: 'orderTypePickupOrder',
  [ORDER_TYPE_DELIVERY]: 'orderTypeDeliveryOrder',
  [ORDER_TYPE_CATERING]: 'orderTypeCateringOrder',
  [ORDER_TYPE_SELF_SERVICE]: 'orderTypeSelfServiceOrder',
};

// Returns the order types (as translation IDs) where an item without posIntegrationRef
// can still be ordered — i.e., enabled configs that don't have POS integration.
// Returns null if the item has posIntegrationRef or if no restriction applies.
export const getOrderTypesWithoutPosIntegration = (store, itemData) => {
  if (!store || !itemData || itemData.posIntegrationRef) return null;
  const entries = Object.entries(ORDER_TYPE_TO_CONFIG_KEY);
  const available = entries
    .filter(([, key]) => {
      const config = store[key];
      return config?.isEnabled && !config.posIntegration;
    })
    .map(([orderType]) => ORDER_TYPE_TO_LABEL_ID[orderType])
    .filter(Boolean);
  // If all enabled configs lack POS (no restriction) or none available, no badge needed
  const enabledConfigs = entries.filter(([, key]) => store[key]?.isEnabled);
  if (available.length === 0 || available.length === enabledConfigs.length)
    return null;
  return available;
};

// Returns the first cart OrderItem whose ItemData OR one of its selected
// ItemAdditions lacks a posIntegrationRef, when the given order config has a POS
// integration. Such items/additions cannot be synced to the POS and are
// therefore not orderable for that order type.
// Returns null if the config has no POS integration or the cart is compatible.
export const getPosConflictCartItem = (orderConfig, cart) => {
  if (!orderConfig?.posIntegration || !cart?.length) return null;
  // Additions only need a ref when the POS actually maps them (ready2order
  // folds additions into the item, so unmapped additions are fine there).
  const additionsNeedRef = posIntegrationRequiresAdditionMapping(
    orderConfig.posIntegration,
  );
  return (
    cart.find(({ itemData, additions }) => {
      if (itemData && !itemData.posIntegrationRef) return true;
      if (!additionsNeedRef) return false;
      return (additions || []).some(
        ({ additionGroupEntry }) =>
          additionGroupEntry?.addition &&
          !additionGroupEntry.addition.posIntegrationRef,
      );
    }) || null
  );
};

// Returns true if the cart contains an item/addition that is incompatible with
// the given order config's POS integration.
export const cartHasPosConflictForConfig = (orderConfig, cart) =>
  !!getPosConflictCartItem(orderConfig, cart);

// Returns the active order config based on context (table, kiosk, or order type)
// When on a table, use inhouseOrderConfig
// When on a kiosk, use the kiosk's order config or fallback to store's config
export const getActiveOrderConfig = (store, orderType, table, kiosk) => {
  if (!store) return null;

  // If on a table (joined via table URL), use the config matching the order type,
  // falling back to inhouse config for inhouse orders or when no order type is specified
  if (table) {
    if (orderType && orderType !== ORDER_TYPE_INHOUSE) {
      return getOrderConfig(store, orderType) || store.inhouseOrderConfig;
    }
    return store.inhouseOrderConfig;
  }

  // If on a kiosk, use kiosk's config or fallback to store's config
  if (kiosk) {
    // Kiosks can have selfServiceOrderConfig and pickupOrderConfig
    if (orderType === ORDER_TYPE_SELF_SERVICE) {
      return kiosk.selfServiceOrderConfig || store.selfServiceOrderConfig;
    }
    if (orderType === ORDER_TYPE_PICKUP) {
      return kiosk.pickupOrderConfig || store.pickupOrderConfig;
    }
  }

  // Otherwise, use the store's config for the given order type
  return getOrderConfig(store, orderType);
};

// Returns available order types based on enabled order configs
export const getAvailableOrderTypesFromConfigs = (store, table, kiosk) => {
  if (!store) return [];

  const availableTypes = [];

  // If on a table, inhouse and self-service are available if enabled
  if (table) {
    // Area configuration has higher priority than order-config
    // If area has orderType set, only that orderType is available (regardless of order-config)
    const areaOrderType = table.area?.orderType;
    if (areaOrderType) {
      // Area forces a specific order type - use it directly
      // The area is the source of truth for tables, so we don't check if the config is enabled
      availableTypes.push(areaOrderType);
      return availableTypes;
    }

    // No area orderType override - use default behavior based on enabled order configs
    if (store.inhouseOrderConfig?.isEnabled) {
      availableTypes.push(ORDER_TYPE_INHOUSE);
    }
    if (store.selfServiceOrderConfig?.isEnabled) {
      availableTypes.push(ORDER_TYPE_SELF_SERVICE);
    }

    // Check if foreign order types are allowed when on a table
    if (store.inhouseOrderConfig?.allowForeignOrderTypes) {
      if (store.pickupOrderConfig?.isEnabled) {
        availableTypes.push(ORDER_TYPE_PICKUP);
      }
      if (store.deliveryOrderConfig?.isEnabled) {
        availableTypes.push(ORDER_TYPE_DELIVERY);
      }
      if (store.cateringOrderConfig?.isEnabled) {
        availableTypes.push(ORDER_TYPE_CATERING);
      }
    }
    return availableTypes;
  }

  // If on a kiosk, check kiosk's configs or fallback to store's configs
  if (kiosk) {
    const selfServiceConfig =
      kiosk.selfServiceOrderConfig || store.selfServiceOrderConfig;
    const pickupConfig = kiosk.pickupOrderConfig || store.pickupOrderConfig;

    if (selfServiceConfig?.isEnabled) {
      availableTypes.push(ORDER_TYPE_SELF_SERVICE);
    }
    if (pickupConfig?.isEnabled) {
      availableTypes.push(ORDER_TYPE_PICKUP);
    }
    return availableTypes;
  }

  // For web ordering (store link without table), check pickup, delivery, catering.
  // Self-service is a kiosk/table ordering concept and must not be offered on store links.
  if (store.pickupOrderConfig?.isEnabled) {
    availableTypes.push(ORDER_TYPE_PICKUP);
  }
  if (store.deliveryOrderConfig?.isEnabled) {
    availableTypes.push(ORDER_TYPE_DELIVERY);
  }
  if (store.cateringOrderConfig?.isEnabled) {
    availableTypes.push(ORDER_TYPE_CATERING);
  }

  return availableTypes;
};

// Returns the checkout method from the order config
// When on a table with posIntegration for inhouse orders and both pay_and_go and order_only are allowed,
// order_only has priority (for own cart ordering). pay_and_go is used when paying from table-cart.
export const getCheckoutMethodFromConfig = (
  orderConfig,
  area,
  { table = null, store = null, orderType = null } = {},
) => {
  // Area can override checkout method
  if (area?.checkoutMethod) {
    return area.checkoutMethod;
  }

  if (!orderConfig) {
    return null;
  }

  // Order configs have allowedCheckoutMethods array
  const allowedMethods = orderConfig.allowedCheckoutMethods || [];
  if (allowedMethods.length === 0) {
    return CHECKOUT_METHOD_ORDER_AND_PAY;
  }

  // When on a table with posIntegration for inhouse orders and both pay_and_go and order_only are allowed,
  // prioritize order_only for own cart ordering (pay_and_go is set explicitly when paying from table-cart)
  const isInhouseOrder =
    orderType === ORDER_TYPE_INHOUSE || (!orderType && table);
  const hasPosIntegration = !!orderConfig?.posIntegration;
  if (
    isInhouseOrder &&
    table &&
    hasPosIntegration &&
    allowedMethods.includes(CHECKOUT_METHOD_PAY_AND_GO) &&
    allowedMethods.includes(CHECKOUT_METHOD_ORDER_ONLY)
  ) {
    return CHECKOUT_METHOD_ORDER_ONLY;
  }

  // Return the first allowed method
  return allowedMethods[0];
};

// Returns the service fee from the order config
export const getServiceFeeFromConfig = (orderConfig) => {
  return orderConfig?.serviceFee || 0;
};

// Returns required customer details from the order config
export const getRequiredCustomerDetails = (orderConfig) => {
  return orderConfig?.requiredCustomerDetails || [];
};

// Check if a specific customer detail is required
export const isCustomerDetailRequired = (orderConfig, detailType) => {
  const required = getRequiredCustomerDetails(orderConfig);
  return required.includes(detailType);
};

// Returns tip settings from the order config
export const getTipSettings = (orderConfig) => {
  if (!orderConfig) {
    return {
      allowTip: false,
      allowTipCustomAmount: false,
      allowTipRoundUp: false,
      tipCategories: [5, 10, 15],
      tipHint: null,
    };
  }

  return {
    allowTip: orderConfig.allowTip ?? true,
    allowTipCustomAmount: orderConfig.allowTipCustomAmount ?? true,
    allowTipRoundUp: orderConfig.allowTipRoundUp ?? true,
    tipCategories: orderConfig.tipCategories || [5, 10, 15],
    tipHint: orderConfig.tipHint,
  };
};

// Returns whether cart comments are allowed
export const isCartCommentAllowed = (orderConfig) => {
  return orderConfig?.allowCartComment ?? true;
};

// Returns the post-order hint from the order config
export const getPostOrderHint = (orderConfig) => {
  return orderConfig?.postOrderHint;
};

// Returns the checkoutMethod for a given orderType using order configs
export const getCheckoutMethod = (orderType, store, area, table = null) => {
  if (area?.checkoutMethod) {
    return area.checkoutMethod;
  }

  // Get from order config
  const orderConfig = getOrderConfig(store, orderType);
  if (orderConfig) {
    return getCheckoutMethodFromConfig(orderConfig, area, {
      table,
      store,
      orderType,
    });
  }

  console.warn(
    `Cannot determine CheckoutMethod: order config not found for orderType="${orderType}"`,
  );
  return null;
};

export const getOrderErrorMessage = (error) => {
  let errorMessageId;

  switch (error?.response?.data?.error) {
    case 'NO_TABLE_CART_SELECTIONS_FOUND':
      errorMessageId = 'noTableCartSelectionsFoundError';
      break;
    case 'DISCOUNT_CODE_NOT_FOUND':
    case 'DISCOUNT_CODE_STORE_INVALID':
      errorMessageId = 'invalidDiscountCodeError';
      break;
    case 'DISCOUNT_CODE_EXPIRED':
      errorMessageId = 'discountCodeExpiredError';
      break;
    case 'STORE_CLOSED_AT_REQUESTED_TIME':
      errorMessageId = 'storeClosedAtRequestedTimeError';
      break;
    case 'STORE_CURRENTLY_CLOSED':
      errorMessageId = 'storeIsClosedNotification';
      break;
    case 'ITEM_DATA_DAILY_LIMIT_EXCEEDED':
      errorMessageId = 'dailyLimitExceeded';
      break;
    case 'ITEM_DATA_ORDER_CONDITION_NOT_SATISFIED':
    case 'ITEM_ADDITION_ORDER_CONDITION_NOT_SATISFIED':
    case 'ITEM_ADDITION_GROUP_ORDER_CONDITION_NOT_SATISFIED':
    case 'ITEM_ADDITION_GROUP_ENTRY_ORDER_CONDITION_NOT_SATISFIED':
      errorMessageId = 'addToCartOrderConditionsError';
      break;
    default:
      errorMessageId = 'defaultResponseOrderError';
      break;
  }

  return errorMessageId;
};

export const getOrderClassesFromItemData = (itemData) => {
  if (itemData?.orderConditions.length > 0) {
    const availableOrderClasses = itemData?.orderConditions.flatMap(
      (orderCondition) => orderCondition.classes,
    );
    const uniqueOrderClasses = [...new Set(availableOrderClasses)];
    return uniqueOrderClasses;
  } else {
    return ['takeaway', 'inhouse'];
  }
};
//this function validates if the item data is available for selected orderType (validates orderConditions.classes) if 3rd argument is passed it will get messageId
export const isOrderConditionClassAvailable = (
  orderType,
  itemData,
  getMessageId,
) => {
  // Without a selected order type there is no context to restrict the item
  // against (e.g. browsing the menu while not on a table, or a store that only
  // offers inhouse). Treat the item as available so we don't render a
  // misleading availability badge (which previously always defaulted to
  // "Takeaway Only" because no class could be matched).
  if (!orderType) {
    return getMessageId ? null : true;
  }

  const orderClasses = getOrderClassesFromItemData(itemData);

  const availableInhouse =
    (orderType === ORDER_TYPE_INHOUSE ||
      orderType === ORDER_TYPE_SELF_SERVICE) &&
    (orderClasses.includes('inhouse') ||
      itemData?.orderConditions?.every(({ isActive }) => !isActive)); //at least 1 must be active otherwise its always available

  const availableTakeaway =
    (orderType &&
      [ORDER_TYPE_PICKUP, ORDER_TYPE_DELIVERY, ORDER_TYPE_CATERING].includes(
        orderType,
      ) &&
      orderClasses.includes('takeaway')) ||
    itemData?.orderConditions?.every(({ isActive }) => !isActive);

  if (availableInhouse) {
    return true;
  } else if (availableTakeaway) {
    return true;
  } else if (getMessageId) {
    return !availableInhouse && orderClasses.includes('takeaway')
      ? 'availableTakeawayLabel'
      : !availableTakeaway && orderClasses.includes('inhouse')
      ? 'availableInhouseLabel'
      : 'noneAvailableLabel';
  } else {
    return false;
  }
};

export const getServiceTimesByPriorty = (orderConditions) => {
  const foundOrderCondition = orderConditions
    ?.sort((a, b) => b.priority - a.priority) // sort DESC by priority (high priority first)
    .find((orderCondition) => orderCondition?.isActive);
  return foundOrderCondition?.serviceWindows;
};

export const fetchAndDownloadFile = async ({
  apiBaseUrl,
  endpoint,
  responseType = 'blob',
  fileType = 'application/octet-stream',
  suggestedFilename,
  onStart = () => {},
  onSuccess = () => {},
  onError = () => {},
  queryParams,
}) => {
  onStart();
  // Use v1 query format - remove unsupported params like join
  const cleanParams = prepareQueryParams(queryParams);
  const queryString = buildV1QueryString(cleanParams);
  try {
    const response = await axios.get(
      `${apiBaseUrl}/${endpoint}${queryString}`,
      {
        responseType,
      },
    );

    const blob = new Blob([response.data], { type: fileType });

    const filename =
      suggestedFilename ||
      response.headers['x-suggested-filename'] ||
      `download.${fileType.split('/')[1]}`;

    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(blobUrl);
    document.body.removeChild(link);

    onSuccess(response);
  } catch (error) {
    onError(error);
  }
};

export const getCurrencySuffix = (currency) => {
  if (!currency) {
    return '€';
  }

  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    currencyDisplay: 'symbol', // or "code" or "name"
  }).format(123);

  return formatted.replace(/\d|,|\.|\s/g, '').trim();
};

// Helper to extract ID from a relation that could be either an object with id or just the id string
const getRelationId = (relation) => {
  if (!relation) return null;
  return typeof relation === 'string' ? relation : relation.id;
};

export const transformMenuEntries = ({
  menuEntries,
  items,
  itemDatas,
  itemAdditions,
  itemAdditionGroups,
  itemCategories,
  foodAllergens,
  foodAdditives,
  foodPreferences,
  foodIngredients,
}) => {
  // Ensure all arrays are defined to prevent errors
  const safeItems = items || [];
  const safeItemDatas = itemDatas || [];
  const safeItemCategories = itemCategories || [];
  const safeFoodAllergens = foodAllergens || [];
  const safeFoodAdditives = foodAdditives || [];
  const safeFoodPreferences = foodPreferences || [];
  const safeFoodIngredients = foodIngredients || [];
  const safeItemAdditionGroups = itemAdditionGroups || [];

  // Determine which items are reachable only through hidden categories. A
  // category is hidden when it has no visible CategoryMenuEntry (the public
  // endpoint strips hidden CategoryMenuEntries, so a category referenced by an
  // item but missing a matching CategoryMenuEntry is hidden). Hiding a category
  // also hides its whole subtree: a visible child of a hidden parent is still
  // unreachable from the menu. An item placed at the menu root or in at least
  // one effectively visible category stays orderable; an item whose only
  // placements are hidden (or under a hidden ancestor) must stay findable but
  // not be orderable.
  const visibleCategoryIds = new Set(
    menuEntries
      .filter(
        (entry) =>
          entry.type === 'CategoryMenuEntry' && entry.displayType !== 'hidden',
      )
      .map((entry) => getRelationId(entry.categoryEntry))
      .filter(Boolean),
  );
  const categoryById = new Map(
    safeItemCategories.map((category) => [category.id, category]),
  );
  const isCategoryEffectivelyVisible = (categoryId, seen = new Set()) => {
    if (!categoryId) return true;
    if (!visibleCategoryIds.has(categoryId)) return false;
    // Defensive: a cycle in the parent chain must not loop forever.
    if (seen.has(categoryId)) return false;
    seen.add(categoryId);
    const parentId = getRelationId(categoryById.get(categoryId)?.parent);
    return !parentId || isCategoryEffectivelyVisible(parentId, seen);
  };
  const itemHasVisiblePlacement = new Map();
  menuEntries.forEach((entry) => {
    if (entry.type !== 'ItemMenuEntry') return;
    const itemId = getRelationId(entry.itemEntry);
    if (!itemId) return;
    const categoryId = getRelationId(entry.category);
    const isVisiblePlacement =
      !categoryId || isCategoryEffectivelyVisible(categoryId);
    itemHasVisiblePlacement.set(
      itemId,
      (itemHasVisiblePlacement.get(itemId) || false) || isVisiblePlacement,
    );
  });
  const isHiddenCategoryOnly = (itemId) =>
    itemHasVisiblePlacement.has(itemId) &&
    !itemHasVisiblePlacement.get(itemId);

  return menuEntries.map((entry) => {
    if (entry.type === 'ItemMenuEntry') {
      // Handle both full object and ID-only relations (from loadRelationIds)
      const itemEntryId = getRelationId(entry.itemEntry);
      const item = itemEntryId
        ? safeItems.find((i) => i.id === itemEntryId) || null
        : null;

      // Use found item, or fall back to inline object, or create minimal item from ID
      // This ensures items still render even if the items endpoint didn't return them
      const baseItem =
        item ||
        (typeof entry.itemEntry === 'object' && entry.itemEntry?.id
          ? entry.itemEntry
          : null) ||
        (itemEntryId ? { id: itemEntryId } : null);

      const enrichedItem = baseItem
        ? {
            ...baseItem,
            data: safeItemDatas
              .filter(
                (itemData) => getRelationId(itemData.item) === baseItem?.id,
              )
              .map((dataRef) => {
                const itemData =
                  safeItemDatas.find((d) => d.id === dataRef.id) || null;
                return itemData
                  ? {
                      ...itemData,
                      hiddenCategoryOnly: isHiddenCategoryOnly(baseItem?.id),
                      additionGroups: safeItemAdditionGroups.filter((group) =>
                        (itemData.additionGroups || []).some(
                          (additionGroup) =>
                            group?.id === getRelationId(additionGroup),
                        ),
                      ),
                      ingredients: safeFoodIngredients.filter((ingredient) =>
                        (itemData.ingredients || []).some(
                          (itemIngredient) =>
                            getRelationId(itemIngredient) === ingredient.id,
                        ),
                      ),
                      allergens: safeFoodAllergens.filter((allergen) =>
                        (itemData.allergens || []).some(
                          (itemAllergen) =>
                            getRelationId(itemAllergen) === allergen.id,
                        ),
                      ),
                      preferences: safeFoodPreferences.filter((preference) =>
                        (itemData.preferences || []).some(
                          (itemPreference) =>
                            getRelationId(itemPreference) === preference?.id,
                        ),
                      ),
                      additives: safeFoodAdditives.filter((additive) =>
                        (itemData.additives || []).some(
                          (itemAdditive) =>
                            getRelationId(itemAdditive) === additive?.id,
                        ),
                      ),
                    }
                  : null;
              })
              .filter(Boolean),
          }
        : null;

      // Handle category relation (could be object or ID string)
      const categoryId = getRelationId(entry.category);
      const category = categoryId
        ? safeItemCategories.find((c) => c.id === categoryId) || entry.category
        : entry.category;

      return {
        id: entry.id,
        displayType: entry.displayType,
        manualRanking: entry.manualRanking,
        category: category,
        itemEntry: enrichedItem,
        type: entry.type,
      };
    }

    if (entry.type === 'CategoryMenuEntry') {
      // Handle both full object and ID-only relations (from loadRelationIds)
      const categoryEntryId = getRelationId(entry.categoryEntry);
      const category = categoryEntryId
        ? safeItemCategories.find((c) => c.id === categoryEntryId) ||
          // Fall back to inline object if categories list doesn't contain it
          (typeof entry.categoryEntry === 'object' && entry.categoryEntry?.id
            ? entry.categoryEntry
            : { id: categoryEntryId })
        : null;

      // Handle category relation (could be object or ID string)
      const parentCategoryId = getRelationId(entry.category);
      const parentCategory = parentCategoryId
        ? safeItemCategories.find((c) => c.id === parentCategoryId) ||
          entry.category
        : entry.category;

      return {
        category: parentCategory,
        id: entry.id,
        displayType: entry.displayType,
        manualRanking: entry.manualRanking,
        categoryEntry: category,
        type: entry.type,
      };
    }

    return entry; // Return unchanged if type is unknown
  });
};

export const transformAdditionGroups = (
  additionGroups,
  additions,
  itemDatas,
) => {
  return additionGroups?.map((additionGroup) => {
    // Find all itemDatas that include this additionGroup in their additionGroups array
    const matchingItemDataIds =
      itemDatas
        ?.filter((itemData) =>
          itemData?.additionGroups?.some(
            (group) => group.id === additionGroup.id,
          ),
        )
        ?.map((itemData) => itemData.id) || [];

    return {
      ...additionGroup,
      itemDataIds: matchingItemDataIds,
      // The public item-addition-groups endpoint returns entries for inactive
      // additions too, while the item-additions endpoint only returns active
      // ones. Entries whose addition cannot be resolved are dropped, so no
      // consumer ever sees an entry without an addition.
      entries: additionGroup?.entries
        ?.map((entry) => ({
          ...entry,
          addition: additions?.find(
            (addition) => addition.id === getRelationId(entry.addition),
          ),
        }))
        ?.filter(({ addition }) => !!addition),
    };
  });
};

export const generateWhatsAppLink = (phoneNumber, intl, orderId) => {
  const message = intl.formatMessage(
    { id: 'whatsappMessageText' },
    { orderId },
  );

  // URL encode the message (handles spaces, special chars, etc.)
  const encodedMessage = encodeURIComponent(message);

  return `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
};

export const extractTextOrNull = (htmlString) => {
  if (!htmlString) return null;

  // Create a temporary DOM element
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlString;

  // Extract and trim the text content
  const text = tempDiv.textContent?.trim();

  // Return text or null if empty
  return text ? text : null;
};
